import type { FruitPollinationVariety } from '@/data/fruitPollination';

export type FruitVarietySize = 'small' | 'medium' | 'large' | 'very large';
export type FruitHarvestTiming = 'early' | 'early-mid' | 'mid' | 'mid-late' | 'late';
export type FruitHarvestZoneBand = 'cold' | 'mid' | 'warm';

export interface FruitVarietyInsight {
  flavorProfile: string;
  texture: string;
  size: FruitVarietySize;
  bestUses: string;
  tradeoff: string;
  harvestTiming: FruitHarvestTiming;
  harvestWindowByZone?: Partial<Record<FruitHarvestZoneBand, string>>;
}

const FRUIT_VARIETY_INSIGHTS: Record<string, FruitVarietyInsight> = {
  honeycrisp: {
    flavorProfile: 'sweet-tart with strong aromatics',
    texture: 'very crisp and juicy',
    size: 'large',
    bestUses: 'fresh eating, lunch apples, and premium dessert fruit',
    tradeoff: 'sauce or baking where a softer, less explosive apple is easier to work with',
    harvestTiming: 'mid-late',
  },
  fuji: {
    flavorProfile: 'very sweet with low acidity',
    texture: 'dense, crisp flesh',
    size: 'medium',
    bestUses: 'fresh snacking and long storage',
    tradeoff: 'people who want a sharper, more tart apple for cider or baking',
    harvestTiming: 'late',
  },
  gala: {
    flavorProfile: 'sweet with light aromatics',
    texture: 'crisp but lighter and less dense than late apples',
    size: 'medium',
    bestUses: 'fresh eating, kid-friendly snacking, and early home harvests',
    tradeoff: 'growers chasing intense acidity, deep storage character, or big baking apples',
    harvestTiming: 'early-mid',
  },
  'golden-delicious': {
    flavorProfile: 'sweet and mellow with soft perfume',
    texture: 'tender-crisp when fresh',
    size: 'medium',
    bestUses: 'fresh eating, baking, and broad kitchen use',
    tradeoff: 'people who want a sharply tart apple or a very hard keeper',
    harvestTiming: 'mid',
  },
  goldrush: {
    flavorProfile: 'high-acid, tart-sweet, and intense',
    texture: 'firm and dense',
    size: 'medium',
    bestUses: 'long storage, cider, and late-season baking',
    tradeoff: 'growers who want something mild, easy, and ready to eat right off the tree',
    harvestTiming: 'late',
  },
  cortland: {
    flavorProfile: 'sweet up front with a brisk finish and gentle perfume',
    texture: 'tender-crisp with good slicing quality',
    size: 'large',
    bestUses: 'fresh slices, salads, and apples that hold color after cutting',
    tradeoff: 'very long storage or growers who prefer a denser late-season apple',
    harvestTiming: 'mid',
  },
  empire: {
    flavorProfile: 'sweet first with a brisk tart edge',
    texture: 'firm and neat rather than lush',
    size: 'medium',
    bestUses: 'fresh eating and mixed-use home orchards',
    tradeoff: 'people who want either a strongly tart specialty apple or a huge dessert apple',
    harvestTiming: 'mid',
  },
  enterprise: {
    flavorProfile: 'tart-sweet with deeper late-season character',
    texture: 'firm and substantial',
    size: 'medium',
    bestUses: 'disease-resistant backyard orchards, storage, and baking',
    tradeoff: 'growers looking for an immediately soft, delicate dessert apple',
    harvestTiming: 'late',
  },
  'crimson-crisp': {
    flavorProfile: 'bright sweet-tart with crisp modern dessert-apple character',
    texture: 'very crisp and firm',
    size: 'medium',
    bestUses: 'fresh eating, salads, and disease-resistant backyard dessert fruit',
    tradeoff: 'growers who want a large old-fashioned baking apple or a softer sauce apple',
    harvestTiming: 'mid',
  },
  'coxs-orange-pippin': {
    flavorProfile: 'rich, aromatic sweetness with classic English dessert character',
    texture: 'tender-crisp and juicy',
    size: 'medium',
    bestUses: 'fresh eating and growers who care most about high dessert quality',
    tradeoff: 'rough handling, long plain storage, or a firm kitchen baking apple',
    harvestTiming: 'mid',
  },
  freedom: {
    flavorProfile: 'sweet-tart with clear orchard bite',
    texture: 'crisp and juicy',
    size: 'medium',
    bestUses: 'fresh eating, disease-resistant home orchards, and mixed kitchen use',
    tradeoff: 'growers chasing elite dessert flavor or a long-keeping winter apple',
    harvestTiming: 'mid',
  },
  gravenstein: {
    flavorProfile: 'lively tart-sweet flavor with strong old-fashioned aroma',
    texture: 'crisp when first picked but quick to soften in the kitchen',
    size: 'large',
    bestUses: 'sauce, baking, cider, and early-season kitchen harvests',
    tradeoff: 'long storage or growers who want a dense late dessert apple',
    harvestTiming: 'early',
  },
  haralson: {
    flavorProfile: 'sharp sweet-tart flavor with sturdy northern-apple character',
    texture: 'firm and crisp',
    size: 'medium',
    bestUses: 'baking, fresh eating, and cold-climate home orchards',
    tradeoff: 'growers who want a low-acid sweet apple or a softer dessert texture',
    harvestTiming: 'mid-late',
  },
  'granny-smith': {
    flavorProfile: 'bracing tart flavor with low sweetness',
    texture: 'very firm and crisp',
    size: 'large',
    bestUses: 'baking, cider, and people who want a very sharp apple',
    tradeoff: 'growers looking for mellow sweetness or aromatic dessert quality straight off the tree',
    harvestTiming: 'late',
  },
  'arkansas-black': {
    flavorProfile: 'rich, winey, and low-key sweet until it mellows in storage',
    texture: 'very dense and firm',
    size: 'medium',
    bestUses: 'storage, cider, and old-style winter fruit',
    tradeoff: 'people who want a quick early dessert apple with instant tenderness',
    harvestTiming: 'late',
  },
  jonagold: {
    flavorProfile: 'rich sweet-tart flavor with notable aromatic lift',
    texture: 'crisp, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating, baking, and growers who want a large full-flavored dessert apple',
    tradeoff: 'very long plain storage or growers who want a dense tart winter apple',
    harvestTiming: 'mid-late',
  },
  'apple:liberty': {
    flavorProfile: 'bright sweet-tart flavor with clean acidity',
    texture: 'crisp and juicy',
    size: 'medium',
    bestUses: 'fresh eating, disease-resistant home orchards, and general backyard use',
    tradeoff: 'growers who want either a giant show apple or the richest heritage dessert flavor',
    harvestTiming: 'mid',
  },
  mcintosh: {
    flavorProfile: 'perfumed sweet-tart flavor with classic old-style apple character',
    texture: 'tender, juicy flesh that softens quickly',
    size: 'medium',
    bestUses: 'fresh eating, sauce, and growers who love soft aromatic apples',
    tradeoff: 'firm baking slices, long storage, or people who want a dense crisp apple',
    harvestTiming: 'mid',
  },
  'ashmeads-kernel': {
    flavorProfile: 'intense tart-sweet flavor with rich russeted complexity',
    texture: 'firm, dense flesh',
    size: 'medium',
    bestUses: 'fresh eating for flavor-focused growers, cider, and storage',
    tradeoff: 'growers who want polished supermarket looks or a mild easy sweet apple',
    harvestTiming: 'late',
  },
  'mutsu-crispin': {
    flavorProfile: 'sweet with mellow tartness and broad old-style character',
    texture: 'crisp, dense flesh',
    size: 'very large',
    bestUses: 'fresh eating, baking, and growers who want huge fruit',
    tradeoff: 'small-space growers or people who want a sharp high-acid apple',
    harvestTiming: 'late',
  },
  'pink-lady': {
    flavorProfile: 'bright sweet-tart flavor with strong aromatics',
    texture: 'very crisp and dense',
    size: 'medium',
    bestUses: 'fresh eating and long-holding modern dessert apples',
    tradeoff: 'growers who want an early crop in cool climates or a softer, quicker-cooking kitchen apple',
    harvestTiming: 'late',
  },
  pristine: {
    flavorProfile: 'sweet with light tartness and fresh early-season character',
    texture: 'crisp and juicy',
    size: 'medium',
    bestUses: 'fresh eating and early-season backyard harvests',
    tradeoff: 'long storage or growers who want a richer more complex late apple',
    harvestTiming: 'early',
  },
  'bramley-seedling': {
    flavorProfile: 'strong, sharply tart cooking character',
    texture: 'firm coarse flesh that breaks down beautifully when cooked',
    size: 'very large',
    bestUses: 'baking, sauce, and traditional cooking',
    tradeoff: 'fresh eating straight from the tree or growers who want a sweet dessert apple',
    harvestTiming: 'late',
  },
  'red-delicious': {
    flavorProfile: 'sweet with very low acidity',
    texture: 'crisp at first but less lively than sharper apples',
    size: 'large',
    bestUses: 'fresh eating for growers who want mild sweetness and classic shape',
    tradeoff: 'baking, cider, or bold tartness and richer depth of flavor',
    harvestTiming: 'late',
  },
  'rome-beauty': {
    flavorProfile: 'mild sweet-tart flavor that improves in cooking',
    texture: 'firm flesh that keeps its shape well',
    size: 'large',
    bestUses: 'baking, roasting, and all-purpose kitchen work',
    tradeoff: 'growers who want a high-impact fresh dessert apple right off the tree',
    harvestTiming: 'late',
  },
  bartlett: {
    flavorProfile: 'classic sweet pear with bright fragrance',
    texture: 'juicy and soft when ripe',
    size: 'large',
    bestUses: 'fresh eating, canning, and the standard dessert-pear experience',
    tradeoff: 'baking jobs where a firmer pear is easier to hold together',
    harvestTiming: 'mid',
  },
  bosc: {
    flavorProfile: 'sweet with honeyed spice',
    texture: 'firm and dry enough to hold shape well',
    size: 'medium',
    bestUses: 'baking, poaching, and sliced cooked pear dishes',
    tradeoff: 'growers who want the juiciest fresh-eating pear straight off the counter',
    harvestTiming: 'mid-late',
  },
  chojuro: {
    flavorProfile: 'sweet with butterscotch notes',
    texture: 'very crisp and juicy',
    size: 'medium',
    bestUses: 'fresh eating and crunchy Asian pear snacks',
    tradeoff: 'soft dessert-pear texture or long simmered cooking uses',
    harvestTiming: 'mid',
  },
  shinseiki: {
    flavorProfile: 'lightly sweet with a crisp, refreshing finish',
    texture: 'crisp and refreshing',
    size: 'medium',
    bestUses: 'fresh eating and reliable backyard Asian pear harvests',
    tradeoff: 'growers who want the deepest perfume or the richest dessert-pear character',
    harvestTiming: 'early-mid',
  },
  comice: {
    flavorProfile: 'very sweet, rich dessert quality',
    texture: 'soft, buttery, and juicy',
    size: 'large',
    bestUses: 'fresh eating and premium dessert-pear harvests',
    tradeoff: 'poaching, baking, or growers who need a firmer kitchen pear',
    harvestTiming: 'mid',
  },
  'd-anjou': {
    flavorProfile: 'sweet with gentle spice and low acidity',
    texture: 'fine-grained and juicy',
    size: 'large',
    bestUses: 'fresh eating, baking, and long useful kitchen storage',
    tradeoff: 'growers who want the softest buttery dessert pear or the crunch of an Asian pear',
    harvestTiming: 'mid-late',
  },
  'golden-spice': {
    flavorProfile: 'sweet with bright tartness and a little spice',
    texture: 'firm flesh',
    size: 'small',
    bestUses: 'preserves, canning, and cold-climate kitchen use',
    tradeoff: 'growers who want a big melting dessert pear for fresh eating',
    harvestTiming: 'late',
  },
  gourmet: {
    flavorProfile: 'sweet with smooth dessert character',
    texture: 'juicy, finer-textured flesh',
    size: 'medium',
    bestUses: 'fresh eating and hardy-climate dessert pears',
    tradeoff: 'growers who want the crunch of an Asian pear or a firmer baking pear',
    harvestTiming: 'mid',
  },
  hosui: {
    flavorProfile: 'sweet with caramel-like richness',
    texture: 'very crisp and juicy',
    size: 'large',
    bestUses: 'fresh eating and growers who want big crunchy Asian pears',
    tradeoff: 'soft European-pear texture or long simmered pear desserts',
    harvestTiming: 'mid',
  },
  'juicy-jewel': {
    flavorProfile: 'sweet with light floral notes',
    texture: 'very juicy and crisp',
    size: 'medium',
    bestUses: 'fresh eating and easy backyard Asian-pear snacking',
    tradeoff: 'growers who want dense cooking pears or deeper old-fashioned pear perfume',
    harvestTiming: 'mid',
  },
  luscious: {
    flavorProfile: 'sweet dessert character with smooth richness',
    texture: 'soft, juicy flesh when ripe',
    size: 'medium',
    bestUses: 'fresh eating and cold-climate dessert pears',
    tradeoff: 'growers who need a firmer poaching pear or the snap of an Asian pear',
    harvestTiming: 'mid',
  },
  moonglow: {
    flavorProfile: 'sweet with soft perfume and low acidity',
    texture: 'soft, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating, canning, and reliable home orchards',
    tradeoff: 'growers chasing the richest dessert flavor or a notably crisp pear',
    harvestTiming: 'mid',
  },
  olympic: {
    flavorProfile: 'sweet, rich flavor with crisp Asian-pear style',
    texture: 'very crisp and dense',
    size: 'very large',
    bestUses: 'fresh eating and growers who want giant crunchy pears',
    tradeoff: 'soft buttery dessert texture or gentle poaching fruit',
    harvestTiming: 'late',
  },
  parker: {
    flavorProfile: 'sweet with mild floral character',
    texture: 'soft, juicy flesh',
    size: 'medium',
    bestUses: 'fresh eating and cold-climate dessert pears',
    tradeoff: 'growers who want very large fruit or a firmer kitchen pear',
    harvestTiming: 'mid',
  },
  patten: {
    flavorProfile: 'sweet with a light tart finish',
    texture: 'soft, juicy flesh',
    size: 'medium',
    bestUses: 'fresh eating, canning, and northern home orchards',
    tradeoff: 'growers chasing luxury dessert texture or Asian-pear crunch',
    harvestTiming: 'mid',
  },
  potomac: {
    flavorProfile: 'sweet with enough acidity to stay lively',
    texture: 'smooth, juicy flesh',
    size: 'medium',
    bestUses: 'fresh eating and disease-conscious home orchards',
    tradeoff: 'growers who want a huge canning pear or very crisp Asian-pear texture',
    harvestTiming: 'mid-late',
  },
  seckel: {
    flavorProfile: 'very sweet, spicy, concentrated flavor',
    texture: 'fine-grained and juicy',
    size: 'small',
    bestUses: 'fresh eating, preserves, and growers who care more about flavor than size',
    tradeoff: 'big slicing fruit or large, firm canning pears',
    harvestTiming: 'mid-late',
  },
  kieffer: {
    flavorProfile: 'lightly sweet with a firm tart edge',
    texture: 'coarse, firm flesh',
    size: 'large',
    bestUses: 'canning, baking, preserves, and tough low-fuss plantings',
    tradeoff: 'growers who want a melting dessert pear for eating out of hand',
    harvestTiming: 'late',
  },
  shinko: {
    flavorProfile: 'sweet, rich flavor with firm Asian-pear style',
    texture: 'crisp and dense',
    size: 'large',
    bestUses: 'fresh eating and growers who want a firm crunchy pear with better keeping quality',
    tradeoff: 'soft buttery texture or a pear that melts down quickly when cooked',
    harvestTiming: 'mid-late',
  },
  summercrisp: {
    flavorProfile: 'light sweetness with a bright acidic snap',
    texture: 'crisp, firm flesh meant for eating before fully soft',
    size: 'medium',
    bestUses: 'fresh eating in cold climates and early pear harvests',
    tradeoff: 'growers who want a melting ripe pear or a deep richly perfumed dessert fruit',
    harvestTiming: 'early',
  },
  'twentieth-century': {
    flavorProfile: 'sweet with light perfume and very low acidity',
    texture: 'crisp, juicy Asian-pear texture',
    size: 'medium',
    bestUses: 'fresh eating and approachable home-garden Asian pears',
    tradeoff: 'growers who want the biggest richest Asian pear or a soft European pear',
    harvestTiming: 'mid',
  },
  ure: {
    flavorProfile: 'mild sweet-tart flavor',
    texture: 'firm flesh with practical kitchen use',
    size: 'small',
    bestUses: 'canning, preserves, and very cold-climate reliability',
    tradeoff: 'growers who want large dessert pears with soft buttery texture',
    harvestTiming: 'early-mid',
  },
  montmorency: {
    flavorProfile: 'bright tart cherry flavor',
    texture: 'juicy with medium flesh',
    size: 'medium',
    bestUses: 'pie, jam, drying, and preserving',
    tradeoff: 'fresh eating if you want a sweet cherry straight from the tree',
    harvestTiming: 'early-mid',
  },
  'north-star': {
    flavorProfile: 'tart with a slightly softer edge than Montmorency',
    texture: 'juicy and tender',
    size: 'small',
    bestUses: 'cold-climate preserving and smaller home plantings',
    tradeoff: 'growers who want the biggest tart cherries or heavy fresh-snacking appeal',
    harvestTiming: 'mid',
  },
  balaton: {
    flavorProfile: 'dark, richly tart, and fuller than classic pie cherries',
    texture: 'meaty and juicy',
    size: 'large',
    bestUses: 'juice, preserves, baking, and growers who want deeper color',
    tradeoff: 'growers who want the brighter, more familiar pie-cherry flavor of Montmorency',
    harvestTiming: 'mid-late',
  },
  meteor: {
    flavorProfile: 'clear tart-cherry flavor',
    texture: 'juicy with softer flesh',
    size: 'small',
    bestUses: 'cold regions, pies, and compact tart-cherry plantings',
    tradeoff: 'growers chasing larger fruit or a darker richer preserving cherry',
    harvestTiming: 'mid',
  },
  bing: {
    flavorProfile: 'deeply sweet and classic dark cherry',
    texture: 'firm and juicy',
    size: 'large',
    bestUses: 'fresh eating and classic market-style sweet cherries',
    tradeoff: 'preserving-heavy kitchens or growers who need a forgiving cold-climate cherry',
    harvestTiming: 'mid',
  },
  rainier: {
    flavorProfile: 'very sweet and delicate',
    texture: 'tender-juicy',
    size: 'large',
    bestUses: 'fresh eating and premium dessert fruit',
    tradeoff: 'growers who want dark-colored sweet cherries for preserves or stronger cherry intensity',
    harvestTiming: 'mid',
  },
  stella: {
    flavorProfile: 'sweet with deep dark-cherry notes',
    texture: 'firm and juicy',
    size: 'large',
    bestUses: 'fresh eating and easier backyard sweet-cherry plantings',
    tradeoff: 'growers who want the palest, sweetest specialty cherry or the sharp pie-cherry bite of tart varieties',
    harvestTiming: 'mid',
  },
  blackgold: {
    flavorProfile: 'sweet with deep black-cherry character',
    texture: 'firm flesh',
    size: 'large',
    bestUses: 'fresh eating and cooler-region sweet-cherry plantings',
    tradeoff: 'growers who want a delicate yellow cherry or a tart preserving cherry',
    harvestTiming: 'mid-late',
  },
  bluecrop: {
    flavorProfile: 'sweet first, then briskly tart',
    texture: 'firm enough for easy picking',
    size: 'medium',
    bestUses: 'fresh eating, freezing, and all-purpose home blueberry use',
    tradeoff: 'growers who care most about giant show berries or very early harvests',
    harvestTiming: 'mid',
  },
  duke: {
    flavorProfile: 'lightly sweet with a crisp tart edge',
    texture: 'firm and tidy',
    size: 'medium',
    bestUses: 'early harvests and fresh eating',
    tradeoff: 'people who want the deepest blueberry flavor or the latest season extension',
    harvestTiming: 'early',
  },
  legacy: {
    flavorProfile: 'sweet with lively acidity',
    texture: 'juicy and fairly firm',
    size: 'medium',
    bestUses: 'fresh eating and season-stretching mixed plantings',
    tradeoff: 'growers who want the earliest berries or the very largest fruit',
    harvestTiming: 'mid-late',
  },
  jersey: {
    flavorProfile: 'sweet with old-style jammy depth',
    texture: 'softer old-style berry texture',
    size: 'medium',
    bestUses: 'processing, jam, and reliable late-season crops',
    tradeoff: 'growers chasing very large fresh-market berries with extra firmness',
    harvestTiming: 'late',
  },
  chandler: {
    flavorProfile: 'sweet with a soft finish',
    texture: 'juicy with a bigger bite because of berry size',
    size: 'very large',
    bestUses: 'fresh eating and growers who care about oversized berries',
    tradeoff: 'people who want the deepest old-style blueberry flavor or the earliest crop',
    harvestTiming: 'mid-late',
  },
  patriot: {
    flavorProfile: 'sweet with a brisk tart snap',
    texture: 'firm, juicy berry texture',
    size: 'large',
    bestUses: 'fresh eating and cold-climate backyard harvests',
    tradeoff: 'growers who want the very latest season extension or the biggest giant-berry novelty fruit',
    harvestTiming: 'early',
  },
  spartan: {
    flavorProfile: 'sweet with strong classic blueberry depth',
    texture: 'firm and juicy',
    size: 'large',
    bestUses: 'fresh eating and premium early-season picking',
    tradeoff: 'growers who want heavy processing fruit or a late freezer crop',
    harvestTiming: 'early',
  },
  berkeley: {
    flavorProfile: 'sweet with softer old-style blueberry character',
    texture: 'soft-juicy berry texture',
    size: 'large',
    bestUses: 'fresh eating and generous home-garden harvest bowls',
    tradeoff: 'growers who want the firmest shipping-style berry or a sharp wild-blueberry edge',
    harvestTiming: 'mid',
  },
  bluejay: {
    flavorProfile: 'lightly sweet with a clean tart edge',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'fresh eating, freezing, and neat easy picking',
    tradeoff: 'growers who want giant berries or especially concentrated jam flavor',
    harvestTiming: 'mid',
  },
  blueray: {
    flavorProfile: 'sweet-tart with stronger old-style blueberry flavor',
    texture: 'juicy with medium firmness',
    size: 'large',
    bestUses: 'fresh eating, baking, and freezer harvests',
    tradeoff: 'growers who want the firmest berry for very clean fresh-market handling',
    harvestTiming: 'mid',
  },
  'blueberry:liberty': {
    flavorProfile: 'sweet with lively acidity and cleaner late-season flavor',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'fresh eating, freezing, and extending the highbush season',
    tradeoff: 'growers who want giant berries or the soft richer flavor of old processing types',
    harvestTiming: 'late',
  },
  elliott: {
    flavorProfile: 'brisk sweet-tart flavor',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'late-season fresh eating and freezer crops after other blueberries finish',
    tradeoff: 'growers who want the sweetest early dessert berry or a very large berry',
    harvestTiming: 'late',
  },
  toro: {
    flavorProfile: 'sweet, full blueberry flavor',
    texture: 'juicy with good picking firmness',
    size: 'very large',
    bestUses: 'fresh eating and growers who want big handsome berries',
    tradeoff: 'the earliest crop or growers who care more about concentrated processing flavor than berry size',
    harvestTiming: 'mid',
  },
  aurora: {
    flavorProfile: 'sweet-tart flavor that holds late in the season',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'late-season fresh eating and extending the freezer crop',
    tradeoff: 'growers who want an early berry or especially large fruit',
    harvestTiming: 'late',
  },
  emerald: {
    flavorProfile: 'sweet with light acidity',
    texture: 'firm, juicy berry texture',
    size: 'large',
    bestUses: 'fresh eating and warm-climate southern highbush harvests',
    tradeoff: 'growers who want wild-style concentrated flavor or a cold-climate berry',
    harvestTiming: 'early',
  },
  jewel: {
    flavorProfile: 'sweet-tart with fuller southern-highbush flavor',
    texture: 'firm berry texture',
    size: 'large',
    bestUses: 'fresh eating and all-purpose warm-climate harvests',
    tradeoff: 'growers who want the earliest possible berry or a soft jam-focused berry',
    harvestTiming: 'mid',
  },
  misty: {
    flavorProfile: 'sweet with mild floral notes',
    texture: 'juicy and tender-firm',
    size: 'large',
    bestUses: 'fresh eating and early warm-climate picking',
    tradeoff: 'growers who want a late berry or especially concentrated preserving flavor',
    harvestTiming: 'early',
  },
  oneal: {
    flavorProfile: 'sweet with notable aromatics',
    texture: 'juicy and fairly soft',
    size: 'large',
    bestUses: 'fresh eating and dessert-style southern highbush berries',
    tradeoff: 'rough handling, long storage, or growers who want a firm processing berry',
    harvestTiming: 'early',
  },
  star: {
    flavorProfile: 'sweet with bright highbush character',
    texture: 'firm berry texture',
    size: 'large',
    bestUses: 'fresh eating and dependable warm-climate backyard harvests',
    tradeoff: 'growers who want the deepest old-style blueberry flavor or a very late crop',
    harvestTiming: 'early',
  },
  'sunshine-blue': {
    flavorProfile: 'sweet with mild tartness',
    texture: 'juicy berry texture',
    size: 'medium',
    bestUses: 'container growing, fresh eating, and small-space harvests',
    tradeoff: 'growers who want giant berries or a heavy freezer crop',
    harvestTiming: 'mid',
  },
  brightwell: {
    flavorProfile: 'sweet rabbiteye flavor with mild tartness',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'fresh eating and dependable rabbiteye harvests',
    tradeoff: 'growers who want giant highbush berries or especially rich jam flavor',
    harvestTiming: 'mid',
  },
  climax: {
    flavorProfile: 'bright sweet-tart rabbiteye flavor',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'early rabbiteye harvests, fresh eating, and freezing',
    tradeoff: 'growers who want the latest season extension or especially large berries',
    harvestTiming: 'early',
  },
  'pink-lemonade': {
    flavorProfile: 'sweet with light citrus-like tang',
    texture: 'juicy berry texture',
    size: 'medium',
    bestUses: 'fresh eating and novelty harvest bowls where color matters',
    tradeoff: 'growers who want classic dark-blue blueberry flavor or heavy processing fruit',
    harvestTiming: 'mid-late',
  },
  powderblue: {
    flavorProfile: 'sweet with classic rabbiteye flavor',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'fresh eating, freezing, and later rabbiteye harvests',
    tradeoff: 'growers who want an early berry or giant fresh-market fruit',
    harvestTiming: 'late',
  },
  premier: {
    flavorProfile: 'sweet rabbiteye flavor with a little tart edge',
    texture: 'firm berry texture',
    size: 'large',
    bestUses: 'fresh eating and early rabbiteye harvests',
    tradeoff: 'growers who want the deepest processing flavor or a very late crop',
    harvestTiming: 'early',
  },
  tifblue: {
    flavorProfile: 'classic sweet rabbiteye flavor',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'freezing, preserves, and dependable rabbiteye backyard crops',
    tradeoff: 'growers who want giant berries or the earliest picking window',
    harvestTiming: 'late',
  },
  chippewa: {
    flavorProfile: 'sweet with mild acidity',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'fresh eating and compact cold-climate plantings',
    tradeoff: 'growers who want the largest berries or a long late-season crop',
    harvestTiming: 'mid',
  },
  northblue: {
    flavorProfile: 'sweet-tart with good wild-blueberry character',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'fresh eating, freezing, and very cold-climate gardens',
    tradeoff: 'growers who want giant highbush berries or the hottest-climate performance',
    harvestTiming: 'mid',
  },
  northcountry: {
    flavorProfile: 'sweet with concentrated berry flavor',
    texture: 'soft-juicy berry texture',
    size: 'small',
    bestUses: 'fresh eating, baking, and cold-climate small-space plantings',
    tradeoff: 'growers who want large berries for fast picking or clean market presentation',
    harvestTiming: 'mid',
  },
  northland: {
    flavorProfile: 'sweet-tart with strong classic blueberry flavor',
    texture: 'medium-firm berry texture',
    size: 'medium',
    bestUses: 'fresh eating, freezing, and jam with a fuller blueberry taste',
    tradeoff: 'growers who want giant berries or very late harvest timing',
    harvestTiming: 'mid',
  },
  polaris: {
    flavorProfile: 'sweet with notable aromatics',
    texture: 'firm berry texture',
    size: 'medium',
    bestUses: 'fresh eating and very cold-climate container or small-garden plantings',
    tradeoff: 'growers who want large berries or a long season of heavy picking',
    harvestTiming: 'early',
  },
  redhaven: {
    flavorProfile: 'classic sweet peach with enough acid to stay lively',
    texture: 'juicy with medium-firm flesh',
    size: 'large',
    bestUses: 'fresh eating, canning, and standard home orchard use',
    tradeoff: 'growers looking for the flattest novelty fruit or the latest possible season extension',
    harvestTiming: 'mid',
  },
  contender: {
    flavorProfile: 'sweet and traditional',
    texture: 'juicy with solid flesh',
    size: 'large',
    bestUses: 'cold-climate orchards and dependable fresh eating',
    tradeoff: 'people who want the most fragrant heritage dessert peach or a novelty shape',
    harvestTiming: 'mid-late',
  },
  elberta: {
    flavorProfile: 'sweet, fragrant, and old-fashioned',
    texture: 'juicy and fairly soft when fully ripe',
    size: 'large',
    bestUses: 'fresh eating, baking, freezing, and classic peach desserts',
    tradeoff: 'growers who need the coldest-climate reliability or a compact early crop',
    harvestTiming: 'late',
  },
  reliance: {
    flavorProfile: 'sweet with a gentle finish and lighter depth',
    texture: 'juicy and somewhat soft',
    size: 'medium',
    bestUses: 'cold zones and backup peach production where winter losses are common',
    tradeoff: 'growers prioritizing the richest dessert quality over sheer reliability',
    harvestTiming: 'early-mid',
  },
  saturn: {
    flavorProfile: 'sweet and aromatic with low acid',
    texture: 'very juicy and tender',
    size: 'medium',
    bestUses: 'fresh eating and standout market baskets',
    tradeoff: 'canning or heavy handling where a standard round peach is easier to manage',
    harvestTiming: 'mid',
  },
  frost: {
    flavorProfile: 'sweet with simple peach character',
    texture: 'juicy with medium-soft flesh',
    size: 'medium',
    bestUses: 'home orchards fighting leaf curl and wanting a practical crop',
    tradeoff: 'growers who only care about elite dessert-peach flavor without disease pressure concerns',
    harvestTiming: 'mid',
  },
  castleton: {
    flavorProfile: 'deep sweetness with prune-like richness',
    texture: 'firm flesh that stays tidy when cut or cooked',
    size: 'medium',
    bestUses: 'drying, baking, prune-style preserves, and structured fresh eating',
    tradeoff: 'growers who want a soft, dripping dessert plum or the earliest plum on the tree',
    harvestTiming: 'late',
    harvestWindowByZone: {
      cold: 'mid to late September',
      mid: 'early to mid September',
      warm: 'late August to early September',
    },
  },
  damson: {
    flavorProfile: 'sharp acidity with rich dark-fruit intensity',
    texture: 'firm small-fruit flesh with a preserving bias',
    size: 'small',
    bestUses: 'jam, jelly, chutney, and cooks who want strong plum intensity',
    tradeoff: 'fresh snacking if you want a sweet, mellow dessert plum',
    harvestTiming: 'late',
    harvestWindowByZone: {
      cold: 'mid to late September',
      mid: 'early to mid September',
      warm: 'late August to early September',
    },
  },
  'green-gage': {
    flavorProfile: 'very sweet, rich, almost honeyed character',
    texture: 'tender, juicy flesh',
    size: 'medium',
    bestUses: 'premium fresh eating, jam, and growers who care most about dessert quality',
    tradeoff: 'heavy hauling, long holding, or batches where you want large, easy-to-pit fruit',
    harvestTiming: 'mid',
    harvestWindowByZone: {
      cold: 'late August to early September',
      mid: 'mid to late August',
      warm: 'early to mid August',
    },
  },
  'italian-prune': {
    flavorProfile: 'sweet, dense, old-style prune richness',
    texture: 'firm flesh that dries and bakes well',
    size: 'medium',
    bestUses: 'drying, baking, preserving, and late-season kitchen plums',
    tradeoff: 'growers who want a juicy early dessert plum for eating out of hand',
    harvestTiming: 'late',
    harvestWindowByZone: {
      cold: 'mid to late September',
      mid: 'early to mid September',
      warm: 'late August to early September',
    },
  },
  'mount-royal': {
    flavorProfile: 'deep sweetness with enough acidity to stay lively',
    texture: 'firm, juicy flesh',
    size: 'medium',
    bestUses: 'fresh eating, preserves, and cold-climate European plum plantings',
    tradeoff: 'growers who want oversized show fruit or very soft dessert flesh',
    harvestTiming: 'mid-late',
    harvestWindowByZone: {
      cold: 'early to mid September',
      mid: 'late August to early September',
      warm: 'mid to late August',
    },
  },
  stanley: {
    flavorProfile: 'sweet, mild flavor with light prune depth',
    texture: 'firm flesh with good slicing structure',
    size: 'large',
    bestUses: 'drying, canning, baking, and dependable all-purpose home harvests',
    tradeoff: 'growers chasing the richest dessert-style sweetness or a very early crop',
    harvestTiming: 'late',
    harvestWindowByZone: {
      cold: 'mid to late September',
      mid: 'early to mid September',
      warm: 'late August to early September',
    },
  },
  beauty: {
    flavorProfile: 'bright sweet-tart balance',
    texture: 'juicy, tender flesh',
    size: 'medium',
    bestUses: 'fresh eating and getting the plum season started early',
    tradeoff: 'growers who want a firm preserving plum or strong winter hardiness',
    harvestTiming: 'early',
    harvestWindowByZone: {
      cold: 'late July to early August',
      mid: 'mid to late July',
      warm: 'late June to mid July',
    },
  },
  burgundy: {
    flavorProfile: 'rich sweetness with low tartness',
    texture: 'very juicy dessert-plum flesh',
    size: 'medium',
    bestUses: 'fresh eating and growers who want a richer Japanese-plum style',
    tradeoff: 'firm canning slices, long holding, or sharply tart preserves',
    harvestTiming: 'mid-late',
    harvestWindowByZone: {
      cold: 'late August to early September',
      mid: 'mid to late August',
      warm: 'late July to mid August',
    },
  },
  'elephant-heart': {
    flavorProfile: 'sweet, complex flavor with a deeper winey edge',
    texture: 'juicy, tender flesh',
    size: 'very large',
    bestUses: 'fresh eating, market-style show fruit, and late-summer dessert platters',
    tradeoff: 'small-batch preserving or growers who need firm fruit for rough handling',
    harvestTiming: 'late',
    harvestWindowByZone: {
      cold: 'early to mid September',
      mid: 'late August to early September',
      warm: 'mid to late August',
    },
  },
  hollywood: {
    flavorProfile: 'tart-sweet flavor with a darker red-flesh note',
    texture: 'juicy, somewhat soft flesh',
    size: 'medium',
    bestUses: 'fresh eating, jam, and growers who like red-fleshed novelty fruit',
    tradeoff: 'firm slicing, long storage, or people who want a very mild sweet plum',
    harvestTiming: 'mid',
    harvestWindowByZone: {
      cold: 'late August',
      mid: 'mid to late August',
      warm: 'early to mid August',
    },
  },
  methley: {
    flavorProfile: 'very sweet, mild flavor',
    texture: 'soft, juicy flesh',
    size: 'medium',
    bestUses: 'fresh eating and early-season backyard snacking',
    tradeoff: 'long keeping, firm baking slices, or growers who want a more complex tart-sweet plum',
    harvestTiming: 'early',
    harvestWindowByZone: {
      cold: 'late July to early August',
      mid: 'mid to late July',
      warm: 'late June to mid July',
    },
  },
  'santa-rosa': {
    flavorProfile: 'rich sweet-tart flavor',
    texture: 'juicy flesh with enough firmness for kitchen work',
    size: 'large',
    bestUses: 'fresh eating, baking, preserves, and all-purpose Japanese plum harvests',
    tradeoff: 'growers who want the very earliest crop or a dry-fleshed prune plum',
    harvestTiming: 'mid',
    harvestWindowByZone: {
      cold: 'mid to late August',
      mid: 'early to mid August',
      warm: 'late July to early August',
    },
  },
  satsuma: {
    flavorProfile: 'deep tart-sweet flavor with berry-like intensity',
    texture: 'juicy red flesh',
    size: 'medium',
    bestUses: 'jam, sauce, fresh eating, and growers who want darker flavor',
    tradeoff: 'very mild dessert-plum eating or long, firm countertop holding',
    harvestTiming: 'mid-late',
    harvestWindowByZone: {
      cold: 'late August to early September',
      mid: 'mid to late August',
      warm: 'early to mid August',
    },
  },
  shiro: {
    flavorProfile: 'mild honey-sweet flavor',
    texture: 'juicy, tender yellow flesh',
    size: 'large',
    bestUses: 'fresh eating and easygoing backyard dessert plums',
    tradeoff: 'growers who want sharp acidity, dark skin, or high-intensity preserving flavor',
    harvestTiming: 'early-mid',
    harvestWindowByZone: {
      cold: 'early to mid August',
      mid: 'late July to early August',
      warm: 'mid to late July',
    },
  },
  alderman: {
    flavorProfile: 'sweet flavor with enough depth to feel richer than most hardy hybrids',
    texture: 'firm, juicy yellow flesh',
    size: 'large',
    bestUses: 'fresh eating, tarts, and batches where you want big, handsome slicing fruit',
    tradeoff: 'quick early harvests, heavy jam work, or growers who prefer a softer dark-fleshed plum',
    harvestTiming: 'late',
    harvestWindowByZone: {
      cold: 'late August to early September',
      mid: 'late August',
      warm: 'mid to late August',
    },
  },
  blackice: {
    flavorProfile: 'very sweet dark-fruit richness',
    texture: 'tender, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating, sauce, and cold-climate growers who want dessert quality early',
    tradeoff: 'long handling, firm baking slices, or people who want the latest plum season possible',
    harvestTiming: 'early',
    harvestWindowByZone: {
      cold: 'mid to late August',
      mid: 'early August',
      warm: 'late July to early August',
    },
  },
  'la-crescent': {
    flavorProfile: 'sweet flavor with a light apricot-like lift',
    texture: 'juicy, tender flesh',
    size: 'medium',
    bestUses: 'fresh eating, preserves, and growers who want bright flavor in a hardy plum',
    tradeoff: 'very large fruit, long shelf life, or dense prune-style kitchen use',
    harvestTiming: 'mid',
    harvestWindowByZone: {
      cold: 'late August',
      mid: 'mid August',
      warm: 'early to mid August',
    },
  },
  pembina: {
    flavorProfile: 'sweet, full flavor',
    texture: 'juicy flesh with enough body for kitchen use',
    size: 'large',
    bestUses: 'fresh eating, cooking, preserves, and broad home-orchard use',
    tradeoff: 'the earliest plum slot or growers who want a very firm, dry-fleshed prune type',
    harvestTiming: 'mid-late',
    harvestWindowByZone: {
      cold: 'late August to early September',
      mid: 'late August',
      warm: 'mid to late August',
    },
  },
  superior: {
    flavorProfile: 'sweet flavor with a bright tart edge',
    texture: 'juicy yellow flesh',
    size: 'large',
    bestUses: 'fresh eating, canning, and hardy-climate dessert plum harvests',
    tradeoff: 'growers who want the richest dark-fruit character or very late European-plum timing',
    harvestTiming: 'mid',
    harvestWindowByZone: {
      cold: 'late August',
      mid: 'mid to late August',
      warm: 'early to mid August',
    },
  },
  toka: {
    flavorProfile: 'very sweet flavor with the classic bubblegum note',
    texture: 'juicy, tender flesh',
    size: 'medium',
    bestUses: 'fresh eating and as a standout pollen partner in hardy plum plantings',
    tradeoff: 'drying, firm baking slices, or growers who want a more restrained flavor profile',
    harvestTiming: 'mid-late',
    harvestWindowByZone: {
      cold: 'late August to early September',
      mid: 'mid to late August',
      warm: 'early to mid August',
    },
  },
  waneta: {
    flavorProfile: 'sweet-tart flavor with a slightly wilder hardy-plum edge',
    texture: 'firm, juicy flesh',
    size: 'medium',
    bestUses: 'fresh eating, preserves, and colder sites where you still want solid dessert fruit',
    tradeoff: 'growers who only want ultra-soft dessert texture or very early ripening',
    harvestTiming: 'mid-late',
    harvestWindowByZone: {
      cold: 'late August to early September',
      mid: 'late August',
      warm: 'mid to late August',
    },
  },
  concord: {
    flavorProfile: 'sweet with deep grapey musk',
    texture: 'juicy slip-skin berries',
    size: 'medium',
    bestUses: 'juice, jelly, and classic seeded fresh eating',
    tradeoff: 'growers who want crisp seedless table grapes or a lighter flavor',
    harvestTiming: 'mid-late',
  },
  niagara: {
    flavorProfile: 'sweet with floral grapey notes',
    texture: 'juicy slip-skin berries',
    size: 'medium',
    bestUses: 'fresh eating, juice, and white grape jelly',
    tradeoff: 'growers who want a firmer seedless grape or darker richer flavor',
    harvestTiming: 'mid',
  },
  vanessa: {
    flavorProfile: 'sweet with a clean berry finish',
    texture: 'tender seedless berries',
    size: 'medium',
    bestUses: 'fresh eating and easy backyard snacking',
    tradeoff: 'growers who want stronger musky flavor or seeded juice grapes',
    harvestTiming: 'mid',
  },
  himrod: {
    flavorProfile: 'very sweet with honeyed notes',
    texture: 'thin-skinned seedless berries',
    size: 'medium',
    bestUses: 'fresh eating and light dessert-style table grapes',
    tradeoff: 'growers who want heavier grape-juice flavor or dark-skinned fruit',
    harvestTiming: 'early-mid',
  },
  'reliance-grape': {
    flavorProfile: 'sweet with a light strawberry note',
    texture: 'tender seedless berries',
    size: 'medium',
    bestUses: 'fresh eating and colder-climate seedless harvests',
    tradeoff: 'growers who want firmer skin for longer holding or dark juice grapes',
    harvestTiming: 'early',
  },
  canadice: {
    flavorProfile: 'sweet with bright red-grape character',
    texture: 'juicy seedless berries',
    size: 'small',
    bestUses: 'fresh eating and compact snack clusters',
    tradeoff: 'growers who want larger berries or stronger juice-grape flavor',
    harvestTiming: 'early-mid',
  },
  mars: {
    flavorProfile: 'sweet with mild grapey depth',
    texture: 'juicy seedless berries with a little slip-skin character',
    size: 'medium',
    bestUses: 'fresh eating and dependable backyard seedless crops',
    tradeoff: 'growers who want the thinnest skins or the richest juice flavor',
    harvestTiming: 'mid',
  },
  marquis: {
    flavorProfile: 'sweet with soft floral richness',
    texture: 'large seedless berries with tender skin',
    size: 'large',
    bestUses: 'fresh eating and growers who want bigger pale grapes',
    tradeoff: 'growers who want the earliest crop or stronger grapey musk',
    harvestTiming: 'late',
  },
  edelweiss: {
    flavorProfile: 'very sweet with tropical lift',
    texture: 'juicy slip-skin berries',
    size: 'medium',
    bestUses: 'fresh eating, juice, and cold-climate white grape harvests',
    tradeoff: 'growers who want crisp seedless texture or darker grape intensity',
    harvestTiming: 'early',
  },
  catawba: {
    flavorProfile: 'sweet-tart with rosy grapey depth',
    texture: 'juicy slip-skin berries',
    size: 'medium',
    bestUses: 'juice, wine, and pink seeded table grapes',
    tradeoff: 'growers who want seedless eating grapes or a very early crop',
    harvestTiming: 'late',
  },
  fredonia: {
    flavorProfile: 'sweet with dark Concord-like depth',
    texture: 'juicy slip-skin berries',
    size: 'large',
    bestUses: 'fresh eating, juice, and heavy home harvests',
    tradeoff: 'growers who want seedless clusters or a lighter table-grape style',
    harvestTiming: 'early-mid',
  },
  steuben: {
    flavorProfile: 'sweet with spicy dark-fruit notes',
    texture: 'juicy seeded berries',
    size: 'medium',
    bestUses: 'fresh eating, juice, and home wine use',
    tradeoff: 'growers who want seedless snacking grapes or the earliest ripening crop',
    harvestTiming: 'mid-late',
  },
  jupiter: {
    flavorProfile: 'sweet with a muscat-like finish',
    texture: 'firm, meaty seedless berries',
    size: 'large',
    bestUses: 'fresh eating and growers who want a richer seedless table grape',
    tradeoff: 'growers who want classic juice-grape character or a very late crop',
    harvestTiming: 'mid',
  },
  'somerset-seedless': {
    flavorProfile: 'sweet with a strawberry-like edge',
    texture: 'tender seedless berries',
    size: 'small',
    bestUses: 'fresh eating and cold-climate red seedless harvests',
    tradeoff: 'growers who want large berries or stronger juice-grape flavor',
    harvestTiming: 'early',
  },
  hayward: {
    flavorProfile: 'sweet-tart with classic green-kiwi richness',
    texture: 'smooth, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating, slicing, and standard storage kiwi harvests',
    tradeoff: 'growers who need a self-fruitful vine or a shorter-season crop',
    harvestTiming: 'late',
  },
  bruno: {
    flavorProfile: 'sweet-tart with a little more perfume than Hayward',
    texture: 'juicy, smooth flesh',
    size: 'large',
    bestUses: 'fresh eating and growers who want a classic fuzzy kiwi crop',
    tradeoff: 'growers who need a self-fruitful vine or very compact growth',
    harvestTiming: 'late',
  },
  jenny: {
    flavorProfile: 'sweet-tart with approachable kiwi flavor',
    texture: 'smooth, juicy flesh',
    size: 'medium',
    bestUses: 'fresh eating and smaller home plantings where self-fruitfulness matters',
    tradeoff: 'growers who want the heaviest classic fuzzy-kiwi crop from a full male-female setup',
    harvestTiming: 'late',
  },
  'cherimoya:el-bumpo': {
    flavorProfile: 'very sweet with rich custard-like tropical depth',
    texture: 'smooth, creamy flesh with a bumpy skin that is easy to peel',
    size: 'large',
    bestUses: 'fresh eating and growers who want a large, sweet California-grown cherimoya',
    tradeoff: 'humid climates or regions outside of coastal California where cherimoya grows poorly',
    harvestTiming: 'mid',
  },
  'cherimoya:pierce': {
    flavorProfile: 'very sweet with exceptionally rich custard flavor - one of the finest cherimoyas',
    texture: 'smooth, creamy flesh',
    size: 'large',
    bestUses: 'fresh eating and growers who want premium dessert-quality cherimoya',
    tradeoff: 'humid climates, shipping, or rough handling - Pierce is a delicate high-quality variety',
    harvestTiming: 'mid',
  },
  'cherimoya:honeyhart': {
    flavorProfile: 'sweet with good cherimoya creaminess and a heart-shaped form',
    texture: 'smooth, creamy flesh',
    size: 'medium',
    bestUses: 'fresh eating and dependable backyard cherimoya harvests',
    tradeoff: 'the absolute richest flavor of premium selections like Pierce, or very large fruit',
    harvestTiming: 'mid',
  },
  'cherimoya:fino-de-jete': {
    flavorProfile: 'intensely sweet with aromatic tropical richness and classic Spanish cherimoya character',
    texture: 'very smooth, creamy flesh',
    size: 'large',
    bestUses: 'fresh eating and growers who want the finest Spanish-style cherimoya flavor',
    tradeoff: 'climates outside coastal Spain or coastal California where this variety performs best',
    harvestTiming: 'mid-late',
  },
  'cherimoya:nata': {
    flavorProfile: 'very sweet with smooth cherimoya richness',
    texture: 'smooth, creamy flesh',
    size: 'medium',
    bestUses: 'fresh eating and all-purpose cherimoya harvests',
    tradeoff: 'the very largest fruit or the most intensely aromatic premium Spanish selections',
    harvestTiming: 'mid',
  },
  'date-palm:deglet-noor': {
    flavorProfile: 'mildly sweet with a clean, lightly caramel finish - the classic commercial date',
    texture: 'semi-dry, firm flesh',
    size: 'medium',
    bestUses: 'fresh eating, cooking, baking, and all-purpose date use',
    tradeoff: 'growers who want the softest, most intensely sweet fresh date off the palm',
    harvestTiming: 'mid-late',
  },
  'date-palm:barhi': {
    flavorProfile: 'very sweet with caramel-honey richness, unique in that it can be eaten at the crunchy yellow stage as well as fully ripe',
    texture: 'soft and yielding when ripe, crisp and apple-like at the yellow khalal stage',
    size: 'medium',
    bestUses: 'fresh eating at both the crunchy and soft ripe stages, and growers who want the most versatile date experience',
    tradeoff: 'long shelf life or dry climates where soft dates store easily',
    harvestTiming: 'mid',
  },
  'date-palm:zahidi': {
    flavorProfile: 'mildly sweet with a nutty, lightly sugary finish',
    texture: 'semi-dry flesh with good storage quality',
    size: 'medium',
    bestUses: 'cooking, baking, drying, and hot dry climates where a reliable semi-dry date is needed',
    tradeoff: 'growers who want the richest, softest fresh-eating date experience',
    harvestTiming: 'mid',
  },
  'date-palm:halawy': {
    flavorProfile: 'very sweet with soft, honey-like richness - one of the softest and sweetest fresh dates',
    texture: 'very soft, moist flesh',
    size: 'small',
    bestUses: 'fresh eating and growers who want a soft sweet date that is ready to eat off the palm',
    tradeoff: 'long storage or shipping, as the very soft flesh does not travel or hold well',
    harvestTiming: 'early-mid',
  },
  'mulberry:pakistan': {
    flavorProfile: 'sweet with dark berry richness and a mild winey edge',
    texture: 'juicy flesh in unusually long berries',
    size: 'large',
    bestUses: 'fresh eating, jam, and growers who want a novelty mulberry with dramatically long fruit',
    tradeoff: 'cold climates above zone 7 or growers who want a quick-bearing compact tree',
    harvestTiming: 'mid',
  },
  'mulberry:dwarf-everbearing': {
    flavorProfile: 'sweet with good dark mulberry depth',
    texture: 'juicy, soft berry flesh',
    size: 'small',
    bestUses: 'containers, small-space growing, and growers who want a compact mulberry with a long picking season',
    tradeoff: 'heavy bulk harvests or full-sized mulberry crops for jam and syrup',
    harvestTiming: 'mid',
  },
  'mulberry:hicks-everbearing': {
    flavorProfile: 'sweet with mild mulberry character that ripens over a long season',
    texture: 'juicy, soft berry flesh',
    size: 'medium',
    bestUses: 'extended fresh eating, long-season picking, and backyard mulberry plantings',
    tradeoff: 'growers who want one intense concentrated flush of fruit rather than a drawn-out season',
    harvestTiming: 'mid',
  },
  'mulberry:kokuso': {
    flavorProfile: 'very sweet with rich, dark Korean mulberry character',
    texture: 'juicy, firm berry flesh',
    size: 'large',
    bestUses: 'fresh eating, jam, wine, and growers who want a large-fruited sweet mulberry',
    tradeoff: 'very cold climates or growers who want an extremely long bearing season',
    harvestTiming: 'mid',
  },
  'mulberry:wellington': {
    flavorProfile: 'sweet with mild white-mulberry character and good fresh-eating quality',
    texture: 'juicy, soft berry flesh',
    size: 'large',
    bestUses: 'fresh eating, baking, and backyard plantings where a sweeter, milder mulberry is preferred',
    tradeoff: 'growers who want the richest dark-mulberry intensity or a very compact tree',
    harvestTiming: 'mid',
  },
  'mulberry:shangri-la': {
    flavorProfile: 'sweet with good dark berry richness suited to warm climates',
    texture: 'juicy, soft berry flesh',
    size: 'medium',
    bestUses: 'fresh eating, jam, and Florida and Gulf Coast backyard mulberry plantings',
    tradeoff: 'cold climates where a more hardy variety performs better',
    harvestTiming: 'early',
  },
  'mulberry:persian': {
    flavorProfile: 'sweet with mild, clean white-mulberry flavor',
    texture: 'juicy, tender berry flesh',
    size: 'medium',
    bestUses: 'fresh eating, baking, and all-purpose backyard mulberry harvests',
    tradeoff: 'the richest, most intense dark-mulberry flavor or a very long extended season',
    harvestTiming: 'mid',
  },
  'passion-fruit:maypop': {
    flavorProfile: 'sweet-tart with tropical passion fruit character, lighter and brighter than tropical types',
    texture: 'juicy pulp with crunchy seeds',
    size: 'medium',
    bestUses: 'fresh eating, juice, jelly, and cold-hardy passion fruit growing in zones 5-9',
    tradeoff: 'growers who want the richest, most intense tropical passion fruit flavor from a warm-climate vine',
    harvestTiming: 'mid-late',
  },
  'mango:kent': {
    flavorProfile: 'sweet with rich tropical depth and very low fiber',
    texture: 'smooth, juicy flesh with minimal fiber',
    size: 'large',
    bestUses: 'fresh eating, smoothies, and all-purpose backyard mango harvests',
    tradeoff: 'the earliest season or growers who want a very compact condo-style tree',
    harvestTiming: 'mid-late',
  },
  'mango:keitt': {
    flavorProfile: 'sweet with clean tropical flavor and very low fiber',
    texture: 'firm, smooth flesh that holds well after picking',
    size: 'very large',
    bestUses: 'fresh eating, slicing, and late-season mango harvests that extend into fall',
    tradeoff: 'early crops or growers who want a smaller-fruited variety easier to manage on a compact tree',
    harvestTiming: 'late',
  },
  'mango:haden': {
    flavorProfile: 'sweet with classic rich Florida-mango aroma',
    texture: 'juicy flesh with some fiber',
    size: 'large',
    bestUses: 'fresh eating and the classic Florida backyard mango experience',
    tradeoff: 'low-fiber smoothie-focused use or growers who want a newer variety with better disease resistance',
    harvestTiming: 'mid',
  },
  'mango:ataulfo-honey': {
    flavorProfile: 'intensely sweet with very low acidity and creamy tropical richness',
    texture: 'smooth, virtually fiberless flesh',
    size: 'small',
    bestUses: 'fresh eating, desserts, and growers who want the creamiest, fiber-free mango experience',
    tradeoff: 'large impressive fruit or heavy-yielding commercial-style production',
    harvestTiming: 'early-mid',
  },
  'mango:alphonso': {
    flavorProfile: 'intensely sweet with deep aromatic complexity - considered among the finest mangoes',
    texture: 'smooth, fiberless flesh with a rich buttery quality',
    size: 'medium',
    bestUses: 'fresh eating and growers who want premium dessert-quality mango flavor',
    tradeoff: 'cool or humid climates where Alphonso is difficult to grow well outside of its native range',
    harvestTiming: 'early',
  },
  'mango:nam-doc-mai': {
    flavorProfile: 'very sweet with floral aroma and rich Thai-mango character',
    texture: 'smooth, fiberless flesh',
    size: 'medium',
    bestUses: 'fresh eating and growers who want one of the finest Southeast Asian dessert mangoes',
    tradeoff: 'humid disease-prone climates or areas outside of zones 10-11',
    harvestTiming: 'mid',
  },
  'mango:carrie': {
    flavorProfile: 'uniquely sweet with a distinctive resinous tropical note unlike other mangoes',
    texture: 'smooth, fiberless flesh',
    size: 'medium',
    bestUses: 'fresh eating and growers who appreciate unusual, complex mango flavor',
    tradeoff: 'people who find the resinous note off-putting, or growers who want a more neutral classic mango',
    harvestTiming: 'early-mid',
  },
  'mango:valencia-pride': {
    flavorProfile: 'very sweet with excellent rich flavor and low fiber',
    texture: 'firm, smooth flesh',
    size: 'very large',
    bestUses: 'fresh eating, slicing, and growers who want show-quality large Florida mangoes',
    tradeoff: 'compact trees or growers who want early-season fruit',
    harvestTiming: 'mid-late',
  },
  'mango:glenn': {
    flavorProfile: 'sweet with good classic mango flavor',
    texture: 'smooth, low-fiber flesh',
    size: 'medium',
    bestUses: 'fresh eating and reliable South Florida backyard mango harvests',
    tradeoff: 'growers who want very large fruit or the most intensely aromatic premium mango character',
    harvestTiming: 'early-mid',
  },
  'mango:manila': {
    flavorProfile: 'sweet with delicate floral notes and very low fiber',
    texture: 'smooth, soft flesh',
    size: 'small',
    bestUses: 'fresh eating and warm-climate growers who want an easy, low-fiber mango',
    tradeoff: 'large fruit for slicing and presentation, or very heavy-yielding production',
    harvestTiming: 'early',
  },
  'mango:julie': {
    flavorProfile: 'sweet with good mango depth',
    texture: 'smooth flesh',
    size: 'small',
    bestUses: 'container growing and small-space warm-climate home orchards',
    tradeoff: 'heavy yields or full-sized fruit - Julie is a dwarf condo mango grown for size, not volume',
    harvestTiming: 'mid',
  },
  'mango:pickering': {
    flavorProfile: 'sweet with a hint of peach-like brightness',
    texture: 'smooth, fiberless flesh',
    size: 'medium',
    bestUses: 'container growing, small patios, and growers who want a productive condo-style mango',
    tradeoff: 'full-sized orchard trees or very large fruit',
    harvestTiming: 'mid',
  },
  'mango:cogshall': {
    flavorProfile: 'sweet with mild classic mango flavor',
    texture: 'smooth, low-fiber flesh',
    size: 'medium',
    bestUses: 'container growing and small-space warm-climate plantings',
    tradeoff: 'very high yields or growers who want the richest, most complex mango flavor in a full-size tree',
    harvestTiming: 'mid',
  },
  'avocado:reed': {
    flavorProfile: 'rich, nutty, and buttery with slightly lower oil content than Hass',
    texture: 'dense, creamy flesh that holds its shape well when cut',
    size: 'very large',
    bestUses: 'fresh eating, slicing, and growers who want impressive large summer avocados',
    tradeoff: 'cool coastal zones where Reed ripens poorly, or growers who want long shelf life after picking',
    harvestTiming: 'mid-late',
  },
  'avocado:pinkerton': {
    flavorProfile: 'rich, creamy flavor with good avocado depth',
    texture: 'smooth, thick flesh with a small seed',
    size: 'large',
    bestUses: 'fresh eating, guacamole, and growers who want a high flesh-to-seed ratio',
    tradeoff: 'very cold-sensitive climates or the classic Hass pebbled appearance',
    harvestTiming: 'late',
  },
  'avocado:fuerte': {
    flavorProfile: 'mild, creamy flavor with classic smooth avocado richness',
    texture: 'smooth, buttery flesh',
    size: 'medium',
    bestUses: 'fresh eating, guacamole, and the classic non-Hass backyard avocado experience',
    tradeoff: 'alternate-bearing tendency and lower oil content than Hass in warm years',
    harvestTiming: 'mid',
  },
  'avocado:bacon': {
    flavorProfile: 'mild, lightly creamy flavor with lower oil content than Hass',
    texture: 'smooth flesh, thinner and softer than Hass',
    size: 'medium',
    bestUses: 'cold-climate avocado growing and home orchards in marginal zones',
    tradeoff: 'rich buttery Hass-style flavor or growers in warm climates where better-tasting varieties thrive',
    harvestTiming: 'early',
  },
  'avocado:zutano': {
    flavorProfile: 'mild, clean flavor with light creaminess',
    texture: 'smooth flesh, thinner than Hass',
    size: 'medium',
    bestUses: 'cold-climate marginal-zone growing and as a type-B pollinizer for other varieties',
    tradeoff: 'rich creamy flavor - Zutano is valued more for cold hardiness and pollination than eating quality',
    harvestTiming: 'early',
  },
  'avocado:mexicola': {
    flavorProfile: 'very rich, intense flavor with high oil content - the most flavorful cold-hardy avocado',
    texture: 'creamy flesh with an edible paper-thin black skin',
    size: 'small',
    bestUses: 'fresh eating whole, cold-climate growing, and growers who want the hardiest avocado with real flavor',
    tradeoff: 'large fruit for slicing and presentation, or very warm climates where richer large varieties outperform it',
    harvestTiming: 'early-mid',
  },
  'calamondin:variegated-calamondin': {
    flavorProfile: 'tart-sweet with classic calamondin citrus punch',
    texture: 'juicy flesh with many seeds',
    size: 'small',
    bestUses: 'ornamental container growing, juice, marinades, and growers who want variegated foliage alongside edible fruit',
    tradeoff: 'heavy juice production or growers who only care about yield rather than the ornamental appeal',
    harvestTiming: 'mid',
  },
  'calamondin:peters': {
    flavorProfile: 'tart-sweet with strong citrus intensity',
    texture: 'juicy flesh',
    size: 'small',
    bestUses: 'juice, marinades, drinks, and all-purpose calamondin harvests',
    tradeoff: 'mild flavor or growers who want an ornamental variegated variety',
    harvestTiming: 'mid',
  },
  'calamondin:dwarf-calamondin': {
    flavorProfile: 'tart-sweet with classic calamondin citrus punch',
    texture: 'juicy flesh',
    size: 'small',
    bestUses: 'containers, small-space growing, and indoor citrus plantings where a compact size is essential',
    tradeoff: 'heavy production volumes or full-sized outdoor trees where compactness is not a priority',
    harvestTiming: 'mid',
  },
  'citron:buddhas-hand': {
    flavorProfile: 'intensely aromatic citrus zest with no juice or pulp - grown entirely for fragrance and rind',
    texture: 'thick, dense rind with no flesh to speak of',
    size: 'large',
    bestUses: 'zesting, candying, infusing spirits, fragrance, and novelty presentation',
    tradeoff: 'juice, fresh eating, or any use that requires edible pulp',
    harvestTiming: 'mid-late',
  },
  'citron:diamante': {
    flavorProfile: 'mildly tart with aromatic citrus rind and thick, sweet pith',
    texture: 'very thick rind with minimal juice sacs',
    size: 'very large',
    bestUses: 'candying, zesting, liqueur production, and classic Italian confectionery',
    tradeoff: 'fresh eating, juice, or everyday cooking citrus',
    harvestTiming: 'mid-late',
  },
  'citron:corsican': {
    flavorProfile: 'aromatic with thick rind and tart juice, similar in style to etrog',
    texture: 'very thick rind, small amount of tart juice',
    size: 'large',
    bestUses: 'zesting, candying, and ritual or specialty citrus use',
    tradeoff: 'fresh eating, juice production, or standard cooking citrus',
    harvestTiming: 'late',
  },
  'kumquat:meiwa': {
    flavorProfile: 'sweet rind and sweeter-than-average flesh - the sweetest kumquat type',
    texture: 'smooth, round fruit eaten whole including the skin',
    size: 'medium',
    bestUses: 'fresh eating whole, snacking, and growers who want the mildest, least tart kumquat',
    tradeoff: 'growers who want classic tart kumquat snap or heavy jam production',
    harvestTiming: 'mid',
  },
  'kumquat:fukushu': {
    flavorProfile: 'sweet rind with balanced flesh flavor',
    texture: 'round, smooth fruit eaten whole',
    size: 'large',
    bestUses: 'fresh eating, ornamental container growing, and all-purpose kumquat harvests',
    tradeoff: 'the very sweetest meiwa-style experience or a very compact plant',
    harvestTiming: 'mid',
  },
  'kumquat:marumi': {
    flavorProfile: 'sweet rind with tart flesh - classic kumquat sweet-tart balance',
    texture: 'round, smooth fruit eaten whole',
    size: 'small',
    bestUses: 'fresh eating whole, preserves, and traditional kumquat use',
    tradeoff: 'growers who want the largest fruit or the sweetest flesh',
    harvestTiming: 'mid',
  },
  'kumquat:centennial-variegated': {
    flavorProfile: 'sweet rind with tart flesh, same as standard kumquat',
    texture: 'fruit eaten whole including the skin',
    size: 'small',
    bestUses: 'ornamental container growing where variegated foliage and striped unripe fruit add visual interest',
    tradeoff: 'maximum production or growers who only care about yield rather than the ornamental appeal',
    harvestTiming: 'mid',
  },
  'mandarin:clementine': {
    flavorProfile: 'very sweet with bright citrus freshness and very low acidity',
    texture: 'juicy, tender segments - nearly seedless when isolated from other pollinizers',
    size: 'medium',
    bestUses: 'fresh eating, snacking, and growers who want the classic easy-peel seedless citrus experience',
    tradeoff: 'very cold climates or growers who want a late-season mandarin',
    harvestTiming: 'early-mid',
  },
  'mandarin:kishu': {
    flavorProfile: 'very sweet with clean citrus brightness',
    texture: 'tender, seedless segments in a small easy-peel fruit',
    size: 'small',
    bestUses: 'fresh snacking, lunch boxes, and growers who want the smallest sweetest seedless mandarin',
    tradeoff: 'large impressive fruit or heavy-yielding processing use',
    harvestTiming: 'early',
  },
  'mandarin:gold-nugget': {
    flavorProfile: 'rich sweetness with complex mandarin depth and low acidity',
    texture: 'juicy, seedless flesh',
    size: 'medium',
    bestUses: 'fresh eating and growers who want a late seedless mandarin with premium flavor',
    tradeoff: 'early-season citrus or growers who want the classic easy-peel clementine style',
    harvestTiming: 'late',
  },
  'mandarin:murcott-honey': {
    flavorProfile: 'very sweet with rich honey-like mandarin depth',
    texture: 'juicy, firm flesh',
    size: 'medium',
    bestUses: 'fresh eating and growers who want one of the sweetest, richest-tasting mandarins',
    tradeoff: 'seedless fruit, as Murcott is seedy, or growers in climates too cool for late-season ripening',
    harvestTiming: 'mid-late',
  },
  'tangerine:sunburst': {
    flavorProfile: 'sweet with bright classic tangerine flavor',
    texture: 'juicy, tender flesh',
    size: 'medium',
    bestUses: 'fresh eating and early-season tangerine harvests',
    tradeoff: 'seedless fruit or growers who want the richest, most complex late-season citrus flavor',
    harvestTiming: 'early-mid',
  },
  'tangerine:pixie': {
    flavorProfile: 'very sweet with mild honey-like flavor and very low acidity',
    texture: 'tender, seedless flesh',
    size: 'small',
    bestUses: 'fresh eating, snacking, and growers who want a very late seedless tangerine',
    tradeoff: 'early-season citrus or growers who want large impressive fruit',
    harvestTiming: 'late',
  },
  'tangerine:page': {
    flavorProfile: 'very sweet with rich, complex mandarin-tangerine flavor',
    texture: 'juicy, tender flesh',
    size: 'medium',
    bestUses: 'fresh eating and growers who want one of the best-tasting mid-season tangerines',
    tradeoff: 'seedless fruit, as Page has seeds, or very cold climates',
    harvestTiming: 'mid',
  },
  'tangerine:minneola': {
    flavorProfile: 'sweet-tart with bold, tangy tangelo flavor and strong aromatic lift',
    texture: 'very juicy flesh',
    size: 'large',
    bestUses: 'fresh eating, juice, and growers who want a distinctive, boldly flavored tangelo',
    tradeoff: 'mild sweet flavor, seedless fruit, or growers in cool coastal areas where Minneola ripens poorly',
    harvestTiming: 'mid',
  },
  'gooseberry:hinnomaki-red': {
    flavorProfile: 'sweet-tart with good red-gooseberry depth and mild aromatic lift',
    texture: 'firm berry with a thin skin',
    size: 'medium',
    bestUses: 'fresh eating, jam, baking, and disease-resistant home plantings',
    tradeoff: 'growers who want the very largest fruit or a purely mild dessert gooseberry',
    harvestTiming: 'mid',
  },
  'gooseberry:invicta': {
    flavorProfile: 'tart-sweet with classic sharp gooseberry bite',
    texture: 'firm flesh with good kitchen structure',
    size: 'large',
    bestUses: 'pies, jam, fool, and heavy-yielding disease-resistant plantings',
    tradeoff: 'fresh eating where you want a sweeter, more mellow gooseberry off the bush',
    harvestTiming: 'mid',
  },
  'gooseberry:jeanne': {
    flavorProfile: 'sweet with mild, refined gooseberry character',
    texture: 'tender flesh',
    size: 'large',
    bestUses: 'fresh eating and dessert-quality gooseberry harvests',
    tradeoff: 'heavy processing use or growers who want the sharpest, most classic tart gooseberry flavor',
    harvestTiming: 'mid',
  },
  'gooseberry:pixwell': {
    flavorProfile: 'mild sweet-tart with lighter gooseberry character',
    texture: 'tender flesh with thin skin',
    size: 'medium',
    bestUses: 'fresh eating, baking, and beginner-friendly plantings with easy picking',
    tradeoff: 'growers who want the richest, most intense old-style gooseberry flavor',
    harvestTiming: 'mid',
  },
  'gooseberry:poorman': {
    flavorProfile: 'very sweet with dessert-quality gooseberry richness',
    texture: 'tender, juicy flesh',
    size: 'medium',
    bestUses: 'fresh eating and American-grown dessert gooseberry harvests',
    tradeoff: 'heavy preserving crops or growers who want the most disease-resistant modern variety',
    harvestTiming: 'mid',
  },
  'gooseberry:whinhams-industry': {
    flavorProfile: 'sweet-tart with rich, classic old-English gooseberry depth',
    texture: 'juicy flesh with good texture',
    size: 'large',
    bestUses: 'fresh eating, baking, jam, and dual-purpose kitchen and dessert use',
    tradeoff: 'very disease-prone climates where an older variety like this struggles with mildew',
    harvestTiming: 'mid',
  },
  'currant:jonkheer-van-tets': {
    flavorProfile: 'bright sweet-tart red currant flavor',
    texture: 'juicy berries on long easy-picking strigs',
    size: 'medium',
    bestUses: 'fresh eating, juice, jelly, and early-season red currant harvests',
    tradeoff: 'growers who want the very latest season or the largest individual berries',
    harvestTiming: 'early',
  },
  'currant:rovada': {
    flavorProfile: 'sweet-tart with excellent full red currant flavor',
    texture: 'firm, juicy berries on very long strigs',
    size: 'large',
    bestUses: 'fresh eating, juice, jelly, and late-season red currant harvests with the best flavor',
    tradeoff: 'early crops or growers who want a smaller compact bush',
    harvestTiming: 'late',
  },
  'currant:pink-champagne': {
    flavorProfile: 'sweet with gentle acidity and delicate mild flavor',
    texture: 'juicy, tender berries',
    size: 'medium',
    bestUses: 'fresh eating, fresh garnish, and growers who want a low-acid currant for snacking',
    tradeoff: 'strong-flavored preserves or growers who want classic dark-red currant intensity',
    harvestTiming: 'mid',
  },
  'currant:white-imperial': {
    flavorProfile: 'very sweet with low acidity - the mildest of the currant types',
    texture: 'juicy, tender berries',
    size: 'medium',
    bestUses: 'fresh eating and growers who want a currant mild enough for people who dislike tart fruit',
    tradeoff: 'sharp-flavored preserves, jelly, or anyone who wants classic currant bite',
    harvestTiming: 'mid',
  },
  'currant:consort': {
    flavorProfile: 'strong, dark black currant flavor with classic intensity',
    texture: 'soft processing-berry texture',
    size: 'medium',
    bestUses: 'juice, syrup, jam, and cold-climate growers who need white pine blister rust immunity',
    tradeoff: 'mild flavor or fresh eating where a softer, sweeter berry is preferred',
    harvestTiming: 'mid',
  },
  'currant:crandall': {
    flavorProfile: 'sweet and aromatic with clove-like spice - distinctly different from standard black currant',
    texture: 'juicy berries',
    size: 'large',
    bestUses: 'fresh eating, jam, and ornamental hedgerow plantings with edible fruit',
    tradeoff: 'classic intense black currant flavor for traditional cordials and preserves',
    harvestTiming: 'mid',
  },
  'currant:titania': {
    flavorProfile: 'strong black currant flavor with good depth',
    texture: 'soft processing-berry texture',
    size: 'large',
    bestUses: 'juice, syrup, jam, and high-yield disease-resistant black currant plantings',
    tradeoff: 'mild-flavored fresh eating or regions where black currant growing is restricted',
    harvestTiming: 'mid',
  },
  'currant:ben-sarek': {
    flavorProfile: 'classic black currant flavor with good balance',
    texture: 'soft processing-berry texture',
    size: 'large',
    bestUses: 'juice, syrup, jam, and small-space plantings where a compact heavy bearer matters',
    tradeoff: 'growers who want a very large spreading bush or the earliest possible crop',
    harvestTiming: 'mid',
  },
  'pawpaw:susquehanna': {
    flavorProfile: 'very sweet, rich tropical flavor with banana and vanilla depth - one of the best-tasting pawpaws',
    texture: 'soft, dense, creamy flesh',
    size: 'very large',
    bestUses: 'fresh eating and growers who want the finest dessert-quality pawpaw available',
    tradeoff: 'long storage, shipping, or regions where Susquehanna ripens inconsistently',
    harvestTiming: 'mid-late',
  },
  'pawpaw:allegheny': {
    flavorProfile: 'sweet with good tropical pawpaw depth',
    texture: 'soft, creamy flesh',
    size: 'large',
    bestUses: 'fresh eating and dependable backyard pawpaw harvests',
    tradeoff: 'very late harvests or growers who want the absolute richest flavor of the named varieties',
    harvestTiming: 'mid',
  },
  'pawpaw:sunflower': {
    flavorProfile: 'sweet with mild, accessible tropical flavor',
    texture: 'soft, creamy flesh',
    size: 'medium',
    bestUses: 'fresh eating, early harvests, and reliable home plantings',
    tradeoff: 'maximum fruit size or the most intense, complex pawpaw flavor of premium selections',
    harvestTiming: 'early-mid',
  },
  'pawpaw:nc-1': {
    flavorProfile: 'sweet with classic rich pawpaw flavor and good balance',
    texture: 'soft, creamy flesh',
    size: 'medium',
    bestUses: 'fresh eating and dependable named-variety pawpaw plantings',
    tradeoff: 'very large show fruit or the very latest ripening season',
    harvestTiming: 'mid',
  },
  'pawpaw:pa-golden': {
    flavorProfile: 'sweet with good pawpaw richness and a lighter, cleaner finish',
    texture: 'soft, creamy yellow flesh',
    size: 'large',
    bestUses: 'fresh eating and backyard pawpaw harvests with good-sized fruit',
    tradeoff: 'the very richest, most complex flavor of premium selections like Susquehanna',
    harvestTiming: 'mid',
  },
  'hardy-kiwi:kens-red': {
    flavorProfile: 'sweet-tart with good hardy-kiwi depth and an attractive red skin',
    texture: 'smooth, juicy flesh',
    size: 'small',
    bestUses: 'fresh eating, snacking, and growers who want a distinctive red-skinned hardy kiwi',
    tradeoff: 'very high yields or growers who only want the sweetest, lowest-acid hardy kiwi',
    harvestTiming: 'mid',
  },
  'hardy-kiwi:geneva': {
    flavorProfile: 'sweet with good clean hardy-kiwi flavor',
    texture: 'smooth, juicy flesh',
    size: 'small',
    bestUses: 'fresh eating and reliable cold-climate hardy-kiwi harvests',
    tradeoff: 'growers who want a self-fertile variety or the most distinctive-looking fruit',
    harvestTiming: 'mid',
  },
  'hardy-kiwi:issai': {
    flavorProfile: 'sweet-tart with bright, clean hardy-kiwi flavor',
    texture: 'smooth, juicy flesh',
    size: 'small',
    bestUses: 'fresh eating, container growing, and small-space plantings where self-fertility matters',
    tradeoff: 'the heaviest crops from a full male-female planting setup',
    harvestTiming: 'mid',
  },
  'jujube:lang': {
    flavorProfile: 'sweet with mild apple-like flavor that deepens as the fruit ripens to red',
    texture: 'crisp when fresh, chewier when dried',
    size: 'large',
    bestUses: 'fresh eating, drying, and all-purpose jujube harvests',
    tradeoff: 'growers who want the most intensely sweet fresh jujube or a very compact tree',
    harvestTiming: 'mid',
  },
  'jujube:sugar-cane': {
    flavorProfile: 'very sweet with crisp apple-like freshness',
    texture: 'crisp, firm flesh',
    size: 'medium',
    bestUses: 'fresh eating and growers who want a top-quality snacking jujube',
    tradeoff: 'drying-focused use or growers who want the very largest fruit',
    harvestTiming: 'mid',
  },
  'jujube:honey-jar': {
    flavorProfile: 'intensely sweet with honey-like richness - one of the best fresh-eating jujubes',
    texture: 'crisp, fine-grained flesh',
    size: 'small',
    bestUses: 'fresh eating and growers who want the sweetest, most flavorful jujube experience',
    tradeoff: 'large fruit for impressive presentation or heavy drying batches',
    harvestTiming: 'early-mid',
  },
  'jujube:shanxi-li': {
    flavorProfile: 'sweet with good full jujube flavor',
    texture: 'crisp flesh',
    size: 'large',
    bestUses: 'fresh eating, drying, and growers who want big attractive jujube fruit',
    tradeoff: 'very early ripening or the absolute sweetest snacking varieties like Honey Jar',
    harvestTiming: 'mid-late',
  },
  'jujube:chico': {
    flavorProfile: 'very sweet with concentrated jujube richness',
    texture: 'crisp, fine-grained flesh',
    size: 'medium',
    bestUses: 'fresh eating and reliable backyard jujube harvests',
    tradeoff: 'very large show fruit or growers who want the best drying jujube',
    harvestTiming: 'mid',
  },
  'jujube:ga-866': {
    flavorProfile: 'sweet with good balanced jujube flavor',
    texture: 'crisp flesh',
    size: 'large',
    bestUses: 'fresh eating, drying, and all-purpose jujube home orchards',
    tradeoff: 'growers chasing the most intensely flavored small snacking varieties',
    harvestTiming: 'mid',
  },
  'jujube:sherwood': {
    flavorProfile: 'sweet with good jujube depth',
    texture: 'crisp flesh',
    size: 'large',
    bestUses: 'fresh eating and productive backyard jujube harvests',
    tradeoff: 'the earliest ripening or the most intensely sweet honey-style flavor',
    harvestTiming: 'mid-late',
  },
  'jujube:so': {
    flavorProfile: 'sweet with mild jujube flavor',
    texture: 'crisp flesh',
    size: 'small',
    bestUses: 'ornamental hedgerows, small-space snacking, and dual-purpose jujube plantings',
    tradeoff: 'heavy fruit crops or growers who want large impressive jujube fruit',
    harvestTiming: 'mid',
  },
  'serviceberry:thiessen': {
    flavorProfile: 'very sweet with mild almond-like depth and low acidity',
    texture: 'tender, juicy berry flesh',
    size: 'large',
    bestUses: 'fresh eating, baking, jam, and growers who want the biggest-fruited serviceberry available',
    tradeoff: 'ornamental purposes or compact spaces, as this variety grows into a large multi-stemmed shrub',
    harvestTiming: 'early-mid',
  },
  'serviceberry:smoky': {
    flavorProfile: 'sweet with a fuller berry richness and that characteristic almond note',
    texture: 'tender, juicy berry flesh',
    size: 'medium',
    bestUses: 'fresh eating, preserves, baking, and reliable heavy crops',
    tradeoff: 'small-space growers or those who want the absolute largest individual berries',
    harvestTiming: 'mid',
  },
  'serviceberry:northline': {
    flavorProfile: 'sweet-tart with a brisk edge that softens when fully ripe',
    texture: 'tender berry texture with good juice',
    size: 'small',
    bestUses: 'jam, freezing, baking, and cold-climate growers who want a heavy-bearing compact plant',
    tradeoff: 'fresh eating where large, mild berries matter most',
    harvestTiming: 'early',
  },
  'serviceberry:honeywood': {
    flavorProfile: 'sweet with mild berry richness and a gentle finish',
    texture: 'tender, juicy berry flesh',
    size: 'large',
    bestUses: 'fresh eating, baking, and growers who want a heavy-producing fruiting variety',
    tradeoff: 'small spaces or purely ornamental plantings where fruiting mass is not the priority',
    harvestTiming: 'mid',
  },
  'serviceberry:regent': {
    flavorProfile: 'sweet with a clean, mild berry flavor',
    texture: 'tender berry texture',
    size: 'medium',
    bestUses: 'fresh eating and small-space or container plantings where a compact fruiting shrub is needed',
    tradeoff: 'heavy processing crops or growers who want the largest individual berries',
    harvestTiming: 'early-mid',
  },
  'serviceberry:martin': {
    flavorProfile: 'sweet with a straightforward mild berry character',
    texture: 'tender berry texture',
    size: 'medium',
    bestUses: 'fresh eating, baking, and dependable cold-climate berry harvests',
    tradeoff: 'growers who want stand-out ornamental fall color or the largest, showiest berries',
    harvestTiming: 'mid',
  },
  'serviceberry:autumn-brilliance': {
    flavorProfile: 'sweet with light berry notes and mild almond depth',
    texture: 'tender berry texture',
    size: 'small',
    bestUses: 'dual-purpose plantings where ornamental value and edible fruit both matter',
    tradeoff: 'growers who want maximum berry yield - this variety is primarily an ornamental with useful fruit as a bonus',
    harvestTiming: 'early-mid',
  },
  'cherry:black-tartarian': {
    flavorProfile: 'very sweet with deep dark-cherry richness',
    texture: 'soft, tender flesh',
    size: 'large',
    bestUses: 'fresh eating and growers who want one of the earliest sweet cherries of the season',
    tradeoff: 'long handling, cooking, or anyone who needs a firm cherry that holds up after picking',
    harvestTiming: 'early',
  },
  'cherry:benton': {
    flavorProfile: 'very sweet with excellent dessert-cherry depth',
    texture: 'firm, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating and premium backyard sweet-cherry harvests',
    tradeoff: 'tart preserving use or growers in very cold climates where late-blooming sweet cherries struggle',
    harvestTiming: 'mid',
  },
  'cherry:danube': {
    flavorProfile: 'sweet-tart with dark, rich Hungarian-cherry character',
    texture: 'firm, meaty flesh',
    size: 'large',
    bestUses: 'fresh eating, baking, juice, and growers who want a versatile sweet-tart cherry',
    tradeoff: 'people who want either a classic mild sweet cherry or a sharply tart pie cherry',
    harvestTiming: 'mid-late',
  },
  'cherry:evans-bali': {
    flavorProfile: 'bright tart flavor with good red-fruit depth',
    texture: 'juicy with medium flesh',
    size: 'medium',
    bestUses: 'pie, jam, juice, and cold-climate growers who need a self-fertile tart cherry',
    tradeoff: 'fresh snacking where you want a sweet, mild cherry',
    harvestTiming: 'mid',
  },
  'cherry:hudson': {
    flavorProfile: 'very sweet with classic dark sweet-cherry richness',
    texture: 'firm, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating and old-style backyard sweet-cherry harvests',
    tradeoff: 'growers who want the very latest season or a self-fertile variety',
    harvestTiming: 'mid',
  },
  'cherry:jubileum': {
    flavorProfile: 'sweet with mild dark-cherry notes',
    texture: 'firm flesh',
    size: 'medium',
    bestUses: 'fresh eating and cold-climate sweet-cherry plantings where self-fertility helps',
    tradeoff: 'growers who want the largest or most intensely flavored premium dessert cherry',
    harvestTiming: 'mid',
  },
  'cherry:lapins': {
    flavorProfile: 'sweet with full dark sweet-cherry character',
    texture: 'firm, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating and backyard sweet-cherry plantings where self-fertile varieties matter',
    tradeoff: 'growers who want the very earliest cherries or a tart cherry for baking',
    harvestTiming: 'mid-late',
  },
  'cherry:mesabi': {
    flavorProfile: 'sweet with a light tart edge and good cold-climate depth',
    texture: 'firm flesh',
    size: 'medium',
    bestUses: 'fresh eating and very cold-climate plantings where most sweet cherries fail',
    tradeoff: 'growers in milder climates who want the most intense sweet-cherry flavor',
    harvestTiming: 'mid',
  },
  'cherry:regina': {
    flavorProfile: 'rich, very sweet flavor with premium dessert quality',
    texture: 'very firm, dense flesh',
    size: 'large',
    bestUses: 'fresh eating, late-season picking, and growers who want top-shelf sweet cherries',
    tradeoff: 'early harvests or people who want a soft, quick-eating cherry',
    harvestTiming: 'late',
  },
  'cherry:sam': {
    flavorProfile: 'sweet with good dark-cherry flavor',
    texture: 'firm flesh',
    size: 'large',
    bestUses: 'fresh eating and as a reliable pollinizer for other sweet cherries',
    tradeoff: 'growers who want a self-fertile variety or the very largest premium cherries',
    harvestTiming: 'mid',
  },
  'cherry:sandra-rose': {
    flavorProfile: 'sweet with mild, fresh cherry notes',
    texture: 'firm flesh',
    size: 'medium',
    bestUses: 'fresh eating and cold-climate sweet-cherry plantings',
    tradeoff: 'growers who want the richest dark-cherry intensity or very large fruit',
    harvestTiming: 'early-mid',
  },
  'cherry:skeena': {
    flavorProfile: 'sweet with full, rich dark-cherry flavor',
    texture: 'firm, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating and self-fertile backyard sweet-cherry plantings',
    tradeoff: 'early harvests or growers in very cold climates where late-season sweet cherries struggle',
    harvestTiming: 'mid-late',
  },
  'cherry:surefire': {
    flavorProfile: 'clear, bright tart-cherry flavor',
    texture: 'very firm flesh',
    size: 'medium',
    bestUses: 'pie, juice, and disease-resistant cold-climate tart-cherry plantings',
    tradeoff: 'fresh snacking or growers who want the richest, darkest tart-cherry color and flavor',
    harvestTiming: 'mid',
  },
  'cherry:sweetheart': {
    flavorProfile: 'sweet with clean, rich dark-cherry character',
    texture: 'firm, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating, late-season picking, and self-fertile backyard sweet-cherry plantings',
    tradeoff: 'early harvests or growers who need the coldest-climate reliability',
    harvestTiming: 'late',
  },
  'cherry:utah-giant': {
    flavorProfile: 'very sweet with classic rich dark-cherry flavor',
    texture: 'firm, juicy flesh',
    size: 'very large',
    bestUses: 'fresh eating and growers who want the biggest, most impressive sweet cherries',
    tradeoff: 'self-fertile plantings or areas with shorter seasons where late-ripening cherries struggle',
    harvestTiming: 'mid',
  },
  'cherry:van': {
    flavorProfile: 'sweet-tart with classic balanced sweet-cherry flavor',
    texture: 'firm flesh',
    size: 'medium',
    bestUses: 'fresh eating and as a reliable pollinizer for other sweet cherries',
    tradeoff: 'growers who want the very largest premium fruit or a self-fertile variety',
    harvestTiming: 'mid',
  },
  'cherry:whitegold': {
    flavorProfile: 'very sweet with low acidity and mild, delicate flavor',
    texture: 'tender, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating, self-fertile plantings, and growers who want a pale yellow sweet cherry',
    tradeoff: 'people who want rich dark-cherry color or a sharper tart edge',
    harvestTiming: 'mid',
  },
  'peach:cresthaven': {
    flavorProfile: 'sweet with good balanced peach character',
    texture: 'firm, freestone flesh',
    size: 'large',
    bestUses: 'canning, fresh eating, and growers who want a firm late-season peach',
    tradeoff: 'growers who want the earliest crop or a very soft aromatic dessert peach',
    harvestTiming: 'mid-late',
  },
  'peach:flordaprince': {
    flavorProfile: 'sweet with tropical low-chill peach character',
    texture: 'juicy flesh',
    size: 'medium',
    bestUses: 'warm-climate and Gulf Coast home orchards where most peaches fail to fruit',
    tradeoff: 'cold-climate growers or people who want a high-chill classic peach with richer flavor',
    harvestTiming: 'early',
  },
  'peach:halehaven': {
    flavorProfile: 'very sweet, rich, and aromatic - a classic backyard peach',
    texture: 'juicy, freestone flesh',
    size: 'large',
    bestUses: 'fresh eating, baking, and the full-flavored dessert-peach experience',
    tradeoff: 'cold-climate reliability or growers who need a very firm canning peach',
    harvestTiming: 'mid',
  },
  'peach:j-h-hale': {
    flavorProfile: 'sweet with deep, classic rich peach flavor',
    texture: 'firm, freestone flesh',
    size: 'very large',
    bestUses: 'fresh eating, market-style show fruit, and growers who want the biggest classic peach',
    tradeoff: 'self-fertile plantings, as this variety needs a pollinizer, and cold-climate reliability',
    harvestTiming: 'mid-late',
  },
  'peach:july-elberta': {
    flavorProfile: 'sweet, fragrant, and classic in the Elberta style',
    texture: 'juicy and moderately firm',
    size: 'large',
    bestUses: 'fresh eating, baking, and growers who want Elberta character earlier in the season',
    tradeoff: 'very long storage or the latest-season harvest slot',
    harvestTiming: 'mid',
  },
  'peach:madison': {
    flavorProfile: 'sweet with traditional peach flavor and good depth',
    texture: 'firm, freestone flesh',
    size: 'medium',
    bestUses: 'cold-climate orchards, fresh eating, and dependable all-purpose home harvests',
    tradeoff: 'growers who want elite aromatic dessert quality or very large show fruit',
    harvestTiming: 'mid',
  },
  'peach:o-henry': {
    flavorProfile: 'very sweet with rich, classic late-season peach flavor',
    texture: 'firm, freestone flesh',
    size: 'large',
    bestUses: 'fresh eating, canning, and late-season backyard peach harvests',
    tradeoff: 'early crops or cold climates where late-maturing peaches struggle to finish',
    harvestTiming: 'mid-late',
  },
  'peach:redskin': {
    flavorProfile: 'sweet with good standard peach character',
    texture: 'firm, semi-freestone flesh',
    size: 'medium',
    bestUses: 'fresh eating, canning, and all-purpose mid-season home orchards',
    tradeoff: 'growers who want elite dessert flavor or the very earliest crop',
    harvestTiming: 'mid',
  },
  'peach:veteran': {
    flavorProfile: 'sweet with dependable, classic peach flavor',
    texture: 'firm, freestone flesh',
    size: 'medium',
    bestUses: 'cold-climate orchards and reliable all-purpose home harvests',
    tradeoff: 'growers chasing elite aromatics, giant fruit, or a specialty flavor profile',
    harvestTiming: 'mid-late',
  },
  'peach:white-lady': {
    flavorProfile: 'very sweet with delicate floral notes and very low acidity',
    texture: 'tender, juicy white flesh',
    size: 'large',
    bestUses: 'fresh eating and growers who want a white-fleshed peach with dessert-style sweetness',
    tradeoff: 'canning, rough handling, or anyone who prefers the classic tart edge of yellow peaches',
    harvestTiming: 'mid',
  },
  'apricot:chinese-mormon': {
    flavorProfile: 'sweet with mild apricot character',
    texture: 'juicy, tender flesh',
    size: 'small',
    bestUses: 'very cold climates and home orchards where hardiness matters more than fruit size',
    tradeoff: 'growers who want large, show-quality apricots or the richest flavor',
    harvestTiming: 'early',
  },
  'apricot:goldcot': {
    flavorProfile: 'sweet-tart with bright apricot flavor',
    texture: 'firm, juicy flesh',
    size: 'medium',
    bestUses: 'fresh eating, jam, drying, and cold-climate apricot plantings',
    tradeoff: 'growers who want the largest, mildest dessert apricot straight from the tree',
    harvestTiming: 'mid',
  },
  'apricot:harcot': {
    flavorProfile: 'sweet with clean, bright apricot flavor',
    texture: 'firm, freestone flesh',
    size: 'large',
    bestUses: 'fresh eating, jam, and disease-resistant backyard apricot harvests',
    tradeoff: 'growers in the driest climates who want the richest heritage apricot character',
    harvestTiming: 'mid',
  },
  'apricot:hargrand': {
    flavorProfile: 'very sweet with excellent dessert-apricot quality',
    texture: 'firm, juicy freestone flesh',
    size: 'very large',
    bestUses: 'fresh eating and growers who want the biggest, finest-quality apricots',
    tradeoff: 'drying or heavy preserving work where smaller firmer fruit is easier to handle',
    harvestTiming: 'mid',
  },
  'apricot:harlayne': {
    flavorProfile: 'sweet with reliable apricot flavor',
    texture: 'firm flesh',
    size: 'medium',
    bestUses: 'cold-climate orchards and dependable all-purpose apricot harvests',
    tradeoff: 'growers who want the very largest or most aromatic heritage apricot',
    harvestTiming: 'mid',
  },
  'apricot:moorpark': {
    flavorProfile: 'rich, complex sweet flavor with old-fashioned aromatic depth',
    texture: 'tender, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating, drying, and growers who want classic heritage apricot flavor',
    tradeoff: 'disease-prone climates or cold orchards where Moorpark struggles with reliability',
    harvestTiming: 'mid-late',
  },
  'apricot:perfection': {
    flavorProfile: 'mild, sweet flavor with gentle apricot character',
    texture: 'firm, juicy freestone flesh',
    size: 'large',
    bestUses: 'fresh eating and growers who want a large, mild all-purpose apricot',
    tradeoff: 'people who want the most intense, complex heritage apricot flavor',
    harvestTiming: 'mid',
  },
  'apricot:rival': {
    flavorProfile: 'sweet with good full apricot flavor',
    texture: 'firm, freestone flesh',
    size: 'large',
    bestUses: 'fresh eating, canning, and all-purpose home apricot orchards',
    tradeoff: 'growers who want the richest heritage flavor or who need the coldest-climate reliability',
    harvestTiming: 'mid',
  },
  'apricot:tilton': {
    flavorProfile: 'tart-sweet with classic old-California apricot intensity',
    texture: 'firm flesh good for drying and cooking',
    size: 'medium',
    bestUses: 'drying, jam, canning, and growers who want a versatile kitchen apricot',
    tradeoff: 'fresh eating where you want a mild, very sweet dessert apricot right off the tree',
    harvestTiming: 'mid-late',
  },
  'nectarine:arctic-glo': {
    flavorProfile: 'very sweet with low acidity and delicate floral notes',
    texture: 'tender, juicy white flesh',
    size: 'medium',
    bestUses: 'fresh eating and growers who want a white-fleshed nectarine with dessert sweetness',
    tradeoff: 'canning, rough handling, or anyone who prefers more classic tart nectarine bite',
    harvestTiming: 'mid',
  },
  'nectarine:flavortop': {
    flavorProfile: 'sweet-tart with outstanding rich nectarine flavor',
    texture: 'firm, freestone flesh',
    size: 'large',
    bestUses: 'fresh eating and growers who want one of the best-flavored backyard nectarines',
    tradeoff: 'cold climates or growers who want a very late crop',
    harvestTiming: 'mid',
  },
  'nectarine:harko': {
    flavorProfile: 'sweet with good nectarine flavor',
    texture: 'firm, freestone flesh',
    size: 'medium',
    bestUses: 'cold-climate orchards and dependable all-purpose nectarine harvests',
    tradeoff: 'growers who want the richest flavor intensity or the largest nectarine fruit',
    harvestTiming: 'mid',
  },
  'nectarine:mericrest': {
    flavorProfile: 'sweet with classic nectarine flavor and good cold-climate character',
    texture: 'firm, freestone flesh',
    size: 'medium',
    bestUses: 'cold-climate home orchards and fresh eating in shorter-season regions',
    tradeoff: 'growers who want the largest, most flavorful nectarines and live in mild climates',
    harvestTiming: 'mid',
  },
  'nectarine:necta-zee': {
    flavorProfile: 'sweet with pleasant nectarine flavor',
    texture: 'juicy flesh',
    size: 'small',
    bestUses: 'containers, small-space plantings, and growers who want a genetic dwarf nectarine',
    tradeoff: 'heavy harvests or full-sized fruit for canning and larger batches',
    harvestTiming: 'mid',
  },
  'nectarine:redgold': {
    flavorProfile: 'sweet-tart with good balanced nectarine flavor',
    texture: 'firm, freestone flesh',
    size: 'large',
    bestUses: 'fresh eating, canning, and mid-to-late-season backyard nectarine harvests',
    tradeoff: 'growers who want early-season fruit or the softest dessert-style nectarine',
    harvestTiming: 'mid-late',
  },
  'nectarine:sunfre': {
    flavorProfile: 'sweet and aromatic',
    texture: 'juicy, tender flesh',
    size: 'medium',
    bestUses: 'fresh eating and early-season home nectarine harvests',
    tradeoff: 'long storage, firm canning use, or growers who want a very late crop',
    harvestTiming: 'early-mid',
  },
  'elderberry:bob-gordon': {
    flavorProfile: 'sweet with rich, dark berry depth',
    texture: 'soft processing-berry texture',
    size: 'small',
    bestUses: 'juice, syrup, wine, and prairie-climate growers who want very high yields',
    tradeoff: 'fresh eating straight from the bush or growers outside the prairie belt',
    harvestTiming: 'mid',
  },
  'elderberry:haschberg': {
    flavorProfile: 'sweet, mild berry flavor with good richness',
    texture: 'soft processing-berry texture',
    size: 'medium',
    bestUses: 'juice, syrup, and commercial-style high-yield plantings',
    tradeoff: 'growers who want the most flavorful wild-style elderberries or extreme cold hardiness',
    harvestTiming: 'mid-late',
  },
  'elderberry:johns': {
    flavorProfile: 'dark, rich flavor with strong elderberry depth',
    texture: 'soft processing-berry texture',
    size: 'small',
    bestUses: 'juice, wine, jelly, and heavy-yielding backyard elderberry plantings',
    tradeoff: 'fresh eating or growers who want the sweetest, mildest elderberry flavor',
    harvestTiming: 'mid',
  },
  'elderberry:marge': {
    flavorProfile: 'dark, rich elderberry flavor',
    texture: 'soft processing-berry texture',
    size: 'small',
    bestUses: 'syrup, juice, and reliable backyard elderberry harvests',
    tradeoff: 'growers who want the very highest yields or the most ornamental appearance',
    harvestTiming: 'mid',
  },
  'elderberry:nova': {
    flavorProfile: 'sweet with good elderberry depth and less bitterness than wild types',
    texture: 'soft processing-berry texture',
    size: 'medium',
    bestUses: 'juice, syrup, wine, and growers who want one of the best-flavored named elderberry varieties',
    tradeoff: 'ornamental use or growers in the driest climates',
    harvestTiming: 'mid',
  },
  'elderberry:ranch': {
    flavorProfile: 'classic dark elderberry flavor',
    texture: 'soft processing-berry texture',
    size: 'small',
    bestUses: 'syrup, juice, and prairie-adapted elderberry plantings',
    tradeoff: 'growers who want the sweetest flavor or the highest individual berry size',
    harvestTiming: 'mid',
  },
  'elderberry:scotia': {
    flavorProfile: 'mild, sweet elderberry flavor',
    texture: 'soft processing-berry texture',
    size: 'small',
    bestUses: 'juice, jam, and compact Canadian-climate elderberry plantings',
    tradeoff: 'maximum yield or the richest, darkest elderberry intensity',
    harvestTiming: 'early-mid',
  },
  'elderberry:wyldewood': {
    flavorProfile: 'sweet with good dark berry richness',
    texture: 'soft processing-berry texture',
    size: 'small',
    bestUses: 'syrup, juice, and southern-adapted backyard elderberry plantings',
    tradeoff: 'very cold climates or growers who want a large-fruited European variety',
    harvestTiming: 'mid',
  },
  'elderberry:york': {
    flavorProfile: 'sweet, mild, and one of the best-flavored American elderberries',
    texture: 'soft processing-berry texture',
    size: 'medium',
    bestUses: 'juice, syrup, jelly, and growers who want the highest yields with the best flavor',
    tradeoff: 'very early harvests or extreme cold-climate performance',
    harvestTiming: 'late',
  },
  'fig:black-mission': {
    flavorProfile: 'very sweet with dark, jammy richness and classic California fig character',
    texture: 'soft, moist flesh',
    size: 'medium',
    bestUses: 'fresh eating, drying, jam, and all-purpose backyard fig harvests',
    tradeoff: 'cold climates above zone 7 or growers who want a very pale flesh with mild flavor',
    harvestTiming: 'mid',
  },
  'fig:celeste': {
    flavorProfile: 'very sweet with honey-like richness and a delicate finish',
    texture: 'soft, fine-textured flesh',
    size: 'small',
    bestUses: 'fresh eating, home drying, and southeastern backyard fig plantings',
    tradeoff: 'growers who want large impressive fruit or heavy preserving quantities',
    harvestTiming: 'mid',
  },
  'fig:chicago-hardy': {
    flavorProfile: 'sweet with mild purple-fig character',
    texture: 'soft flesh',
    size: 'medium',
    bestUses: 'cold-climate container growing and growers who want the most reliable fig north of zone 6',
    tradeoff: 'the richest, most intensely sweet fig flavor, as cold-climate survival comes at some cost to flavor depth',
    harvestTiming: 'mid',
  },
  'fig:desert-king': {
    flavorProfile: 'very sweet with rich strawberry-colored flesh and excellent Pacific Northwest character',
    texture: 'soft, juicy flesh',
    size: 'large',
    bestUses: 'fresh eating and cool-summer climates like the Pacific Northwest where most figs ripen poorly',
    tradeoff: 'hot, dry climates or growers who want a dark-skinned fig for drying',
    harvestTiming: 'early',
  },
  'fig:kadota': {
    flavorProfile: 'mild, clean sweetness with a lighter, less intense flavor than dark varieties',
    texture: 'firm flesh relative to other figs',
    size: 'medium',
    bestUses: 'canning, preserving, and growers who want a milder commercial-style fig',
    tradeoff: 'growers who want rich sweet dessert figs for fresh eating right off the tree',
    harvestTiming: 'mid-late',
  },
  'fig:lsu-purple': {
    flavorProfile: 'sweet with good purple-fig richness',
    texture: 'soft flesh',
    size: 'medium',
    bestUses: 'humid southern climates and Gulf Coast home orchards where fig rust is a concern',
    tradeoff: 'cold climates or growers who want a finer-flavored dessert fig in drier conditions',
    harvestTiming: 'mid',
  },
  'fig:violette-de-bordeaux': {
    flavorProfile: 'intensely sweet with raspberry-like richness and premium dessert quality',
    texture: 'soft, fine-grained flesh',
    size: 'small',
    bestUses: 'fresh eating, container growing, and growers who want the richest-flavored small fig',
    tradeoff: 'large-batch preserving or growers who want big fruit for impressive presentation',
    harvestTiming: 'mid',
  },
  'persimmon:fuyu': {
    flavorProfile: 'sweet with mild honey-like flavor - eaten crisp like an apple, no astringency',
    texture: 'firm, crisp flesh when ripe',
    size: 'medium',
    bestUses: 'fresh eating, slicing, salads, and growers who want a non-astringent persimmon',
    tradeoff: 'growers who prefer the most intense, concentrated persimmon sweetness that only develops in soft astringent types',
    harvestTiming: 'mid',
  },
  'persimmon:hachiya': {
    flavorProfile: 'intensely sweet with rich, custardy depth when fully soft - must be fully ripe before eating',
    texture: 'very soft, jelly-like flesh when fully ripe',
    size: 'very large',
    bestUses: 'fresh eating when fully ripe, baking, smoothies, and drying',
    tradeoff: 'growers who want to eat fruit firm off the tree - Hachiya is astringent and must be very soft before eating',
    harvestTiming: 'mid-late',
  },
  'persimmon:izu': {
    flavorProfile: 'sweet with good non-astringent persimmon flavor',
    texture: 'firm, crisp flesh when ripe',
    size: 'medium',
    bestUses: 'fresh eating and small-space growers who want a compact non-astringent persimmon tree',
    tradeoff: 'maximum fruit size or the richest, deepest persimmon sweetness',
    harvestTiming: 'mid',
  },
  'persimmon:prok': {
    flavorProfile: 'very sweet with classic American persimmon intensity when fully ripe',
    texture: 'soft flesh when ripe, astringent until fully ripe',
    size: 'small',
    bestUses: 'fresh eating after full ripeness, jam, pulp, and cold-climate native-fruit plantings',
    tradeoff: 'growers who want large, non-astringent fruit that can be eaten firm off the tree',
    harvestTiming: 'mid-late',
  },
  'persimmon:prairie-star': {
    flavorProfile: 'sweet with good American persimmon character when ripe',
    texture: 'soft flesh when ripe',
    size: 'small',
    bestUses: 'very cold climates and native-plant enthusiasts who want a hardy American persimmon',
    tradeoff: 'large fruit, non-astringent texture, or the convenience of eating firm like Asian types',
    harvestTiming: 'mid',
  },
  'persimmon:saijo': {
    flavorProfile: 'very sweet with refined Japanese persimmon flavor',
    texture: 'soft flesh when ripe, mildly astringent until then',
    size: 'medium',
    bestUses: 'fresh eating, drying, and growers who want one of the sweetest Japanese persimmons',
    tradeoff: 'non-astringent types that can be eaten crisp, or very large show fruit',
    harvestTiming: 'mid-late',
  },
  'persimmon:szukis': {
    flavorProfile: 'very sweet with concentrated American persimmon flavor when ripe',
    texture: 'soft flesh when fully ripe',
    size: 'small',
    bestUses: 'cold-climate fresh eating after ripeness and native-fruit home orchards',
    tradeoff: 'large fruit, non-astringent texture, or growers who want an Asian-style persimmon',
    harvestTiming: 'mid',
  },
  'persimmon:yates': {
    flavorProfile: 'sweet with classic wild-style American persimmon richness when fully ripe',
    texture: 'very soft when fully ripe',
    size: 'small',
    bestUses: 'fresh eating, pulp, and late-season cold-hardy persimmon harvests',
    tradeoff: 'large non-astringent fruit or growers who want an Asian persimmon experience',
    harvestTiming: 'late',
  },
  'quince:aromatnaya': {
    flavorProfile: 'sweet-tart with strong quince aroma and low enough tannin to eat fresh',
    texture: 'firm flesh that is softer and less astringent than most quinces',
    size: 'medium',
    bestUses: 'fresh eating, preserves, and growers who want a quince that can be enjoyed off the tree',
    tradeoff: 'cooking-only use where a sharper, more traditional quince paste flavor is expected',
    harvestTiming: 'mid',
  },
  'quince:champion': {
    flavorProfile: 'mildly sweet with classic quince aroma',
    texture: 'firm flesh that softens and sweetens with cooking',
    size: 'large',
    bestUses: 'jelly, paste, poaching, and all-purpose kitchen quince work',
    tradeoff: 'fresh eating straight from the tree or the most intensely aromatic quince varieties',
    harvestTiming: 'mid-late',
  },
  'quince:meechs-prolific': {
    flavorProfile: 'richly aromatic with strong classic quince character',
    texture: 'firm flesh suited to preserving and cooking',
    size: 'large',
    bestUses: 'jelly, marmalade, paste, and growers who want heavy crops from an ornamental tree',
    tradeoff: 'fresh eating or growers who want a smaller-fruited more compact plant',
    harvestTiming: 'mid',
  },
  'quince:pineapple': {
    flavorProfile: 'mildly sweet with a subtle tropical note and lighter quince character',
    texture: 'firm flesh',
    size: 'large',
    bestUses: 'preserves, canning, and growers who want a milder, less astringent quince',
    tradeoff: 'growers who want the strongest, most traditional quince fragrance and depth',
    harvestTiming: 'mid',
  },
  'quince:smyrna': {
    flavorProfile: 'highly aromatic with deep, complex quince character',
    texture: 'firm flesh',
    size: 'large',
    bestUses: 'jelly, paste, poaching, and classic Mediterranean-style preserving',
    tradeoff: 'cold climates where Smyrna struggles, or growers who want a very mild fresh-eating quince',
    harvestTiming: 'mid-late',
  },
  'quince:vranja': {
    flavorProfile: 'intensely aromatic with the strongest quince perfume of common named varieties',
    texture: 'firm flesh',
    size: 'very large',
    bestUses: 'jelly, paste, baking, and growers who want the most aromatic and impressive quince available',
    tradeoff: 'small spaces, since Vranja grows vigorously, or growers who want low-effort mild preserving fruit',
    harvestTiming: 'late',
  },
  'serviceberry:rainbow-pillar': {
    flavorProfile: 'sweet with mild berry flavor',
    texture: 'tender berry texture',
    size: 'small',
    bestUses: 'narrow spaces, hedgerows, and mixed plantings where the columnar habit is more important than crop volume',
    tradeoff: 'heavy fruiting - this cultivar is grown first for its upright form and fall color, with fruit as a secondary benefit',
    harvestTiming: 'early',
  },
  'honeyberry:pirika': {
    flavorProfile: 'balanced sweet-tart with clean berry brightness',
    texture: 'firm berry flesh with good storage - holds quality 2 or more weeks refrigerated',
    size: 'large',
    bestUses: 'fresh eating and late-season Japanese haskap harvests paired with Keiko, Taka, or Tana',
    tradeoff: 'growers who want to pair it with early Russian or USask varieties like Borealis or Tundra - all Thompson haskap types bloom a month or more after the early group and will not overlap',
    harvestTiming: 'late',
  },
  'honeyberry:borealis': {
    flavorProfile: 'very sweet with low bitterness and a clean blueberry-grape finish',
    texture: 'soft and fragile - berries bruise easily after picking',
    size: 'large',
    bestUses: 'fresh eating straight from the shrub; not suited to shipping or long storage',
    tradeoff: 'growers who need berries that hold up to handling, freezing, or mechanical harvest - the soft texture falls apart quickly',
    harvestTiming: 'mid',
  },
  'honeyberry:tundra': {
    flavorProfile: 'tart-to-sweet with a firm, clean flavor and less sweetness than Borealis',
    texture: 'firm and dense - holds up well to harvest and light processing',
    size: 'large',
    bestUses: 'fresh eating, freezing, and jam; the firm texture makes it the best USask variety for mechanical harvest',
    tradeoff: 'growers who want maximum sweetness - Tundra is more tart than Borealis or Aurora; choose Borealis for the sweetest early harvest',
    harvestTiming: 'early',
  },
  'honeyberry:boreal-blizzard': {
    flavorProfile: 'sweet with classic honeyberry complexity - more than twice the berry weight of Tundra or Borealis',
    texture: 'firm flesh; berries hold on the plant longer than most USask varieties',
    size: 'very large',
    bestUses: 'fresh eating, freezing, and commercial-scale harvests where berry size matters',
    tradeoff: 'growers who want to use Aurora as the companion - Aurora shows poor fruit set with Blizzard per USask controlled crosses; use Boreal Beast as the primary companion instead',
    harvestTiming: 'mid',
  },
  'honeyberry:indigo-gem': {
    flavorProfile: 'sweet and chewy with good balance - less tart than Tundra, less fragile than Borealis',
    texture: 'chewy, medium-firm berry flesh',
    size: 'medium',
    bestUses: 'fresh eating and mixed home berry plantings; the most productive of the Indigo series',
    tradeoff: 'growers who want the largest berries - Indigo Gem is the most reliable of its series but not the biggest; Boreal Blizzard or Happy Giant win on size',
    harvestTiming: 'early',
  },
  'honeyberry:indigo-treat': {
    flavorProfile: 'sweet with a hint of tartness similar to Tundra but slightly milder',
    texture: 'firm berry flesh',
    size: 'medium',
    bestUses: 'fresh eating and early-season harvests where Tundra is unavailable',
    tradeoff: 'growers who want the largest possible berries - Indigo Treat is similar to Tundra but about 10 percent smaller',
    harvestTiming: 'early',
  },
  'honeyberry:keiko': {
    flavorProfile: 'balanced sweet-tart with clean flavor and no bitterness',
    texture: 'medium-firm berry flesh with excellent shelf life - holds 4 or more weeks refrigerated',
    size: 'large',
    bestUses: 'fresh eating, refrigerated storage, and late-season Pacific Northwest haskap growing',
    tradeoff: 'growers who want the most extreme cold-hardiness below zone 3 - Keiko is suited to zones 3 to 7 but the USask series is better tested in the coldest winters',
    harvestTiming: 'late',
  },
  'honeyberry:berry-blue': {
    flavorProfile: 'tart with sharp acidity and concentrated berry flavor - more mouth-puckering than sweeter USask varieties',
    texture: 'soft, small berries with a thin skin',
    size: 'small',
    bestUses: 'jam, juice, and serving as a pollinizer for Borealis, Tundra, and the Indigo series',
    tradeoff: 'fresh eating - the pronounced tartness and small size make it less appealing straight from the shrub compared to USask or Berries Unlimited varieties',
    harvestTiming: 'early',
  },
  'honeyberry:blue-forest': {
    flavorProfile: 'lightly sweet with a clean tart edge - less intensely flavored than the USask varieties but pleasant and refreshing',
    texture: 'firm berries on a compact 2-3 ft. spreading shrub with ornamental deep-green foliage',
    size: 'large',
    bestUses: 'fresh eating, jams, and ornamental-edible plantings where foliage appeal matters; compatible with the early USask group as a companion',
    tradeoff: 'growers who want the most intensely flavored honeyberry - Blue Forest is milder than Borealis, Aurora, or the Berries Unlimited varieties; it is valued partly for its ornamental habit',
    harvestTiming: 'early',
  },
  'honeyberry:honeybee': {
    flavorProfile: 'tart-to-sweet with cylindrical elongated berries and mild complexity',
    texture: 'firm flesh; holds on the plant longer than most varieties without dropping',
    size: 'medium',
    bestUses: 'serving as the primary pollinizer for all early USask varieties; useful fresh eating crop as well',
    tradeoff: 'growers who want the most intensely sweet or largest-fruited honeyberry - Honeybee is bred first for pollination coverage, second for eating quality',
    harvestTiming: 'mid',
  },
  'honeyberry:aurora': {
    flavorProfile: 'deep, rich, and chewy - the most complex flavor of the USask series with notes of blueberry, grape, and mild tartness',
    texture: 'dense, chewy flesh; berries are large and pointed',
    size: 'very large',
    bestUses: 'fresh eating, jam, and growers who want maximum flavor complexity from their honeyberry',
    tradeoff: 'growers who want to pair Boreal Blizzard as the companion - Aurora and Blizzard show poor fruit set together; choose Honeybee or Boreal Beast for Blizzard instead',
    harvestTiming: 'mid',
  },
  'honeyberry:indigo-yum': {
    flavorProfile: 'sweet with mild tartness and a pleasant honeyberry complexity',
    texture: 'firm, slightly boxy-shaped berry with good density',
    size: 'very large',
    bestUses: 'fresh eating; the largest-fruited of the Indigo series and a reliable early-season producer',
    tradeoff: 'growers who want an easy-to-propagate variety - Indigo Yum is notably difficult to propagate from cuttings compared to other USask selections',
    harvestTiming: 'early',
  },
  'honeyberry:boreal-beast': {
    flavorProfile: 'honey, blueberry, and raspberry notes with a well-balanced sweet-acidic finish',
    texture: 'firm, narrow-oval berries with good density',
    size: 'medium',
    bestUses: 'fresh eating and growers who want a bridge pollinizer that overlaps both Boreal Blizzard and Boreal Beauty',
    tradeoff: 'growers who want maximum berry size above all else - Boreal Beast is valued more for its flavor and pollination timing than for competing with Blizzard or Happy Giant on raw weight',
    harvestTiming: 'mid',
  },
  'honeyberry:blue-banana': {
    flavorProfile: 'very sweet with prominent smoked-strawberry and tropical notes - the most unusual flavor in the honeyberry lineup',
    texture: 'juicy, soft flesh; elongated banana-like shape',
    size: 'very large',
    bestUses: 'fresh eating and impressing visitors with a honeyberry unlike anything from the USask series',
    tradeoff: 'growers who want the most extreme cold hardiness in zones 2 to 4 - Blue Banana is rated to zone 2 but the Berries Unlimited types are less tested in extreme cold than USask varieties',
    harvestTiming: 'mid',
  },
  'honeyberry:blue-moose': {
    flavorProfile: 'large, chewy, and sweet with good berry depth and low bitterness',
    texture: 'chewy and dense; vigorous shrub produces large clusters',
    size: 'large',
    bestUses: 'fresh eating and abundant harvests; the dome-shaped crown to 5 ft suits medium to large spaces',
    tradeoff: 'growers who want to fit their honeyberry in a tight space - Blue Moose grows vigorously to 5 ft and wider; Blue Mist or Berry Smart Blue are the better choice for small gardens or containers',
    harvestTiming: 'mid',
  },
  'honeyberry:happy-giant': {
    flavorProfile: 'sweet with a light tartness and clean berry finish - some of the largest berries in the honeyberry world',
    texture: 'firm-juicy flesh; berries exceed 1 inch in length',
    size: 'very large',
    bestUses: 'fresh eating where berry size is the priority; excellent yields by years 4 to 5',
    tradeoff: 'growers who want high yields in the first two or three years - Happy Giant is notably slow to establish and takes four to five years to reach full productivity compared to USask varieties',
    harvestTiming: 'mid',
  },
  'honeyberry:taka': {
    flavorProfile: 'mild, almost purely sweet flavor with very low bitterness - the most approachable honeyberry for people new to the fruit',
    texture: 'cylindrical medium-large berries with tender flesh',
    size: 'medium',
    bestUses: 'fresh eating, especially for people unfamiliar with honeyberry who want a gentle introduction to the flavor; pairs with Keiko and Tana',
    tradeoff: 'growers who want pronounced complexity or tartness - Taka is mild by design, which some growers find less interesting than Aurora or Berry Blue',
    harvestTiming: 'late',
  },
  'honeyberry:boreal-beauty': {
    flavorProfile: 'very sweet with rich honeyberry complexity; the Boreal series at its most developed',
    texture: 'very large, firm berries that cling to the plant well without dropping',
    size: 'very large',
    bestUses: 'fresh eating, freezing, and extending the honeyberry season a full month past the early varieties',
    tradeoff: 'growers who want their first harvest as soon as possible - Boreal Beauty ripens the latest of any USask variety, arriving well after Borealis and Tundra',
    harvestTiming: 'late',
  },
  'honeyberry:blue-hokkaido': {
    flavorProfile: 'sweet-tart with crisp, clean flavor and a slight refreshing bite',
    texture: 'crisp, firm flesh - the crispest texture in the honeyberry lineup',
    size: 'very large',
    bestUses: 'fresh eating where crisp texture is preferred over the softer chew of USask varieties',
    tradeoff: 'growers who want a variety with extensive extreme-cold field testing below zone 3 - Blue Hokkaido is a Japanese type rated to zone 3, but USask varieties have more proven performance in the coldest winters',
    harvestTiming: 'late',
  },
  'honeyberry:blue-mist': {
    flavorProfile: 'sweet-tart with delicate berry flavor; pleasant but less intense than the USask or Berries Unlimited selections',
    texture: 'large berries on a compact, spreading plant reaching only 2 to 3 ft',
    size: 'large',
    bestUses: 'smaller gardens, container growing, and mixed fruit plantings where a compact late-season honeyberry fits the space',
    tradeoff: 'growers who want maximum yield per plant - the dwarf habit limits total productivity compared to full-sized shrubs',
    harvestTiming: 'late',
  },
  'honeyberry:blue-pacific': {
    flavorProfile: 'large, sweet, mildly flavored berries with a clean finish - lighter flavor than most late-season types',
    texture: 'large, light-blue berries on a compact 2 to 3 ft spreading shrub',
    size: 'large',
    bestUses: 'small gardens, late-season fresh eating, and as a pollinizer for other Kuril-type late bloomers',
    tradeoff: 'growers who want the most intensely flavored late-season berry - Blue Pacific is pleasant but mild compared to Blue Velvet or Giant\'s Heart',
    harvestTiming: 'late',
  },
  'honeyberry:blue-pagoda': {
    flavorProfile: 'mild flavor without the sweetness or complexity of the leading varieties',
    texture: 'small to medium berries on a cold-hardy shrub',
    size: 'medium',
    bestUses: 'serving as a late-season pollinizer for Blue Velvet, Blue Sea, and other Kuril-types; grown for pollination coverage rather than eating quality',
    tradeoff: 'fresh eating or jam - Blue Pagoda is planted for its pollination timing, not its fruit quality, which most sources rate as inferior to other honeyberries',
    harvestTiming: 'late',
  },
  'honeyberry:giants-heart': {
    flavorProfile: 'very sweet with a distinctive smoked-prune finish unlike any other honeyberry variety',
    texture: 'large oval heart-shaped berries with dense flesh',
    size: 'large',
    bestUses: 'fresh eating where flavor novelty is prized; pairs naturally with Strawberry Sensation for two back-to-back unusual flavor profiles',
    tradeoff: 'growers who want purely sweet or purely tart - Giant\'s Heart has a savory smoked note that some palates love and others find unexpected',
    harvestTiming: 'late',
  },
  'honeyberry:strawberry-sensation': {
    flavorProfile: 'sweet-tart with a distinctive strawberry-like finish that makes it recognizable as honeyberry while tasting unlike it',
    texture: 'dark blue, medium-firm berries on a very tall shrub reaching 8 to 9 ft',
    size: 'medium',
    bestUses: 'fresh eating for the flavor novelty and as a late-season crop that extends the harvest well past early varieties',
    tradeoff: 'growers who want a compact honeyberry for small spaces - Strawberry Sensation grows into one of the tallest shrubs in the lineup, reaching 8 to 9 ft.',
    harvestTiming: 'late',
  },
  'honeyberry:blue-moon': {
    flavorProfile: 'mild, lightly sweet flavor typical of Kuril-type berries; less complex than USask or Berries Unlimited selections',
    texture: 'berries stay green inside even when fully ripe externally - a useful and unique ripeness indicator',
    size: 'medium',
    bestUses: 'pairing with Blue Velvet or Blue Sea as a late-season companion; the internal-color ripeness signal is a useful harvesting cue',
    tradeoff: 'growers who want outstanding eating quality from their late-season variety - Blue Moon is planted more for pollination pairing and the ripeness-indicator trait than for best-in-class flavor',
    harvestTiming: 'late',
  },
  'honeyberry:blue-sea': {
    flavorProfile: 'sweet, clean flavor with good complexity for a dwarf Japanese selection',
    texture: 'very large, crisp berries on a very compact 2 to 3 ft spreading plant',
    size: 'very large',
    bestUses: 'containers, small garden beds, and late-season fresh eating where a dwarf plant with large fruit is the priority',
    tradeoff: 'heavy total harvests - the dwarf habit limits yield per plant despite the large individual berry size',
    harvestTiming: 'late',
  },
  'honeyberry:blue-velvet': {
    flavorProfile: 'sweet, clean, and crisp - considered the best-flavored among the late Kuril-type varieties',
    texture: 'large, firm, crisp berries; ornamental grey-green velvety foliage',
    size: 'large',
    bestUses: 'fresh eating, ornamental mixed plantings where the distinctive foliage adds visual interest, and late-season harvests',
    tradeoff: 'growers who need the widest zone 2 to 3 cold hardiness tested - Blue Velvet is rated to zone 2 but the USask series has more field-testing in extreme cold',
    harvestTiming: 'late',
  },
  'honeyberry:tana': {
    flavorProfile: 'tart-sweet with clean Japanese haskap character and no bitterness',
    texture: 'oval medium-large berries with outstanding firmness; holds 24 or more days refrigerated without quality loss',
    size: 'large',
    bestUses: 'fresh eating with extended fridge storage, CSA or market growing where shelf life matters, and Pacific Northwest late-season harvests',
    tradeoff: 'growers who want the most extreme cold hardiness below zone 3 - Tana is a Thompson/OSU Japanese variety suited to zones 3 to 8 and less tested in extreme continental cold than USask selections',
    harvestTiming: 'late',
  },
  'honeyberry:blue-horn': {
    flavorProfile: 'sweet with mild honeyberry character',
    texture: 'medium-sized berries',
    size: 'medium',
    bestUses: 'fresh eating and cross-pollination pairing with mid-season companions',
    tradeoff: 'growers who need well-documented variety performance data - published information on Blue Horn is limited compared to USask or Berries Unlimited varieties',
    harvestTiming: 'mid',
  },
  'hazelnut:yamhill': {
    flavorProfile: 'mild, clean hazelnut flavor with good sweetness and low astringency',
    texture: 'medium kernel with thin shell; good kernel-to-shell ratio',
    size: 'medium',
    bestUses: 'fresh cracking, roasting, and home orchards where EFB-resistant early varieties are the priority',
    tradeoff: 'growers who want the largest possible nut - Yamhill produces reliably but not as large as Ennis or Hall\'s Giant',
    harvestTiming: 'early',
  },
  'hazelnut:daviana': {
    flavorProfile: 'mild hazelnut flavor typical of traditional European selections',
    texture: 'medium kernel',
    size: 'medium',
    bestUses: 'pollinizer for Barcelona in EFB-free orchards; not recommended where Eastern Filbert Blight is present',
    tradeoff: 'any planting where EFB is a risk - Daviana is highly susceptible and has been largely replaced by resistant alternatives like Gamma and Eta',
    harvestTiming: 'early',
  },
  'hazelnut:barcelona': {
    flavorProfile: 'rich, classic hazelnut flavor with full sweetness - the benchmark for traditional hazelnut taste',
    texture: 'medium-large kernel; familiar commercial hazelnut texture',
    size: 'large',
    bestUses: 'traditional hazelnut orchards, roasting, chocolate pairing, and home gardens in EFB-free regions',
    tradeoff: 'growers who want EFB resistance - Barcelona is highly susceptible to Eastern Filbert Blight and will decline in any planting where the disease is present',
    harvestTiming: 'early',
  },
  'hazelnut:butler': {
    flavorProfile: 'mild hazelnut flavor; decent but not the most complex eating quality',
    texture: 'medium kernel',
    size: 'medium',
    bestUses: 'serving as a pollinizer for Barcelona and Ennis in traditional orchards',
    tradeoff: 'growers who want a premium eating hazelnut - Butler is grown primarily for its pollination value rather than nut quality',
    harvestTiming: 'early',
  },
  'hazelnut:the-beast': {
    flavorProfile: 'full, rich hazelnut flavor with good kernel size from its 75 percent European parentage',
    texture: 'large, well-filled kernel; vigorous tree to 20 ft',
    size: 'large',
    bestUses: 'eastern and midwestern home orchards where EFB is present and a large productive hybrid tree is needed',
    tradeoff: 'growers who want a compact tree that fits a smaller yard - The Beast grows into a substantial tree reaching 20 ft and needs room; Grand Traverse is the better choice for tighter spaces',
    harvestTiming: 'early',
  },
  'hazelnut:clark': {
    flavorProfile: 'mild, clean hazelnut flavor',
    texture: 'small kernel with high yield per plant',
    size: 'small',
    bestUses: 'high-volume home harvests and orchards where early nut drop makes collection easier',
    tradeoff: 'growers who want the largest possible nut - Clark is one of the smallest-nutted OSU varieties; pair Lewis with something other than Clark since they share incompatible S-alleles',
    harvestTiming: 'early',
  },
  'hazelnut:casina': {
    flavorProfile: 'rich hazelnut flavor with an exceptionally high kernel-to-shell ratio - one of the best eating nuts when healthy',
    texture: 'large round kernel; thin shell',
    size: 'large',
    bestUses: 'confectionery, premium roasting, and orchards in EFB-free regions where nut quality is the top priority',
    tradeoff: 'planting where EFB is present, or growers who need confirmed bloom timing data - Casina is susceptible to EFB and its bloom timing varies enough across sources that local verification is recommended',
    harvestTiming: 'early',
  },
  'hazelnut:lewis': {
    flavorProfile: 'mild, pleasant hazelnut flavor - the most understated of the OSU selections',
    texture: 'medium kernel with clean mild character',
    size: 'medium',
    bestUses: 'EFB-resistant home orchards where mild flavor and reliable crops matter more than bold taste; do not pair with Clark (incompatible S-alleles)',
    tradeoff: 'growers who want the most intense or complex hazelnut flavor - Lewis is reliably mild rather than richly flavored',
    harvestTiming: 'mid',
  },
  'hazelnut:dorris': {
    flavorProfile: 'clean hazelnut flavor with good eating quality',
    texture: 'medium kernel on a compact 10 to 12 ft tree',
    size: 'medium',
    bestUses: 'smaller home orchards where a compact EFB-resistant tree is needed; pairs with York or Felix',
    tradeoff: 'large-scale planting or growers who want the biggest nuts - Dorris is compact and reliable rather than a high-volume commercial producer',
    harvestTiming: 'mid',
  },
  'hazelnut:york': {
    flavorProfile: 'clean, pleasant hazelnut flavor with good kernel fill',
    texture: 'medium kernel; compact tree habit',
    size: 'medium',
    bestUses: 'EFB-resistant home orchards as a reliable mid-season producer and general pollinizer; avoid pairing with Jefferson (shared S-allele)',
    tradeoff: 'growers who want to pollinate Jefferson - York and Jefferson share an S-allele and cannot cross-pollinate each other; use Eta, Theta, or Felix for Jefferson instead',
    harvestTiming: 'mid',
  },
  'hazelnut:wepster': {
    flavorProfile: 'good kernel quality with clean hazelnut character',
    texture: 'medium kernel',
    size: 'medium',
    bestUses: 'EFB-resistant home orchards; pairs well with McDonald and York',
    tradeoff: 'growers who want extensively documented S-allele compatibility data - Wepster\'s specific incompatibilities are less published than Jefferson, York, or Lewis',
    harvestTiming: 'mid',
  },
  'hazelnut:gamma': {
    flavorProfile: 'mild hazelnut flavor typical of OSU pollinizer releases',
    texture: 'medium kernel',
    size: 'medium',
    bestUses: 'serving as the primary pollinizer for Yamhill; OSU EFB-resistant pollinizer release',
    tradeoff: 'growers who want an eating-first hazelnut - Gamma is a dedicated pollinizer release and not selected for premium eating quality',
    harvestTiming: 'mid',
  },
  'hazelnut:grand-traverse': {
    flavorProfile: 'clean hazelnut flavor with good sweetness from its European and Turkish tree hazel parentage',
    texture: 'medium kernel on a compact 10 to 15 ft tree',
    size: 'medium',
    bestUses: 'eastern home orchards as a near-universal pollinizer; S11 and S25 alleles make it broadly compatible with most EFB-resistant varieties including The Beast',
    tradeoff: 'growers who want a large, heavy-bearing fruiting tree - Grand Traverse\'s primary role is pollination; it bears nuts but is less productive than Jefferson or Yamhill as a fruiting selection',
    harvestTiming: 'mid',
  },
  'hazelnut:jefferson': {
    flavorProfile: 'rich, full hazelnut flavor - the flagship OSU selection for both eating quality and EFB resistance',
    texture: 'large, well-filled kernel with excellent kernel-to-shell ratio',
    size: 'large',
    bestUses: 'premium home orchards, roasting, and confectionery where both EFB resistance and top eating quality matter',
    tradeoff: 'growers who want to skip pollinizer research - Jefferson requires late-shedding companions (Eta, Theta, or Felix) and York cannot pollinate it due to a shared S-allele; the wrong pairing produces almost nothing',
    harvestTiming: 'late',
  },
  'hazelnut:ennis': {
    flavorProfile: 'rich, intense hazelnut flavor with the largest nut size of any common variety',
    texture: 'very large kernel; the benchmark for impressive nut size',
    size: 'very large',
    bestUses: 'gourmet eating, premium confectionery, and orchards in EFB-free regions where maximum nut size is the goal',
    tradeoff: 'growers who want EFB resistance - Ennis is highly susceptible to Eastern Filbert Blight and will decline badly in any region where the disease is established',
    harvestTiming: 'late',
  },
  'hazelnut:halls-giant': {
    flavorProfile: 'solid traditional hazelnut flavor with impressive nut size',
    texture: 'large kernel; sheds abundant pollen late season',
    size: 'large',
    bestUses: 'late pollinizer for Ennis and Jefferson; also produces a respectable eating crop in EFB-free orchards',
    tradeoff: 'planting where EFB is present - Hall\'s Giant lacks the resistance of OSU varieties',
    harvestTiming: 'late',
  },
  'hazelnut:eta': {
    flavorProfile: 'mild hazelnut flavor; grown primarily as a pollinizer for Jefferson',
    texture: 'medium kernel',
    size: 'medium',
    bestUses: 'mid-to-late pollinizer for Jefferson; one of the three OSU-recommended companions for Jefferson alongside Theta and Felix',
    tradeoff: 'growers who want an eating-first variety - Eta is selected for its late pollen timing rather than for nut quality',
    harvestTiming: 'late',
  },
  'hazelnut:felix': {
    flavorProfile: 'clean hazelnut flavor with decent eating quality',
    texture: 'medium kernel',
    size: 'medium',
    bestUses: 'serving as the earliest of Jefferson\'s three recommended pollinizers; pairs well with Dorris as well',
    tradeoff: 'growers who want a premium standalone eating hazelnut - Felix is chosen for pollination timing over nut quality',
    harvestTiming: 'late',
  },
  'hazelnut:delta': {
    flavorProfile: 'mild hazelnut flavor typical of OSU pollinizer releases',
    texture: 'medium kernel',
    size: 'medium',
    bestUses: 'mid-to-late pollinizer for Lewis and Clark; OSU EFB-resistant pollinizer release',
    tradeoff: 'growers who want a premium eating-quality nut - Delta is a dedicated pollinizer release chosen for pollen timing, not nut flavor or size',
    harvestTiming: 'late',
  },
  'hazelnut:epsilon': {
    flavorProfile: 'mild hazelnut flavor typical of OSU pollinizer releases',
    texture: 'medium kernel',
    size: 'medium',
    bestUses: 'late pollinizer covering the mid-through-late female bloom of Lewis and Clark; OSU EFB-resistant pollinizer release',
    tradeoff: 'growers who want a premium eating-quality nut - Epsilon is a dedicated pollinizer release chosen for pollen timing, not nut flavor or size',
    harvestTiming: 'late',
  },
  'hazelnut:theta': {
    flavorProfile: 'mild hazelnut flavor; grown specifically as the latest pollinizer for Jefferson',
    texture: 'medium kernel',
    size: 'medium',
    bestUses: 'serving as the latest-pollen-shedding OSU release, essential for reliably pollinating Jefferson when its female receptivity extends late in the season',
    tradeoff: 'growers who want a premium eating-quality nut rather than a dedicated pollinizer - Theta exists solely to fill Jefferson\'s late pollination window and is not selected for flavor or size',
    harvestTiming: 'late',
  },
  'raspberry:prelude': {
    flavorProfile: 'bright, intensely flavored with strong raspberry character - the classic benchmark for fresh red raspberry taste',
    texture: 'medium-soft; berries are not built for long handling',
    size: 'medium',
    bestUses: 'fresh eating, early-season jam, and gardens where getting the first raspberry of summer is the goal',
    tradeoff: 'growers who want a high-volume fall harvest - Prelude\'s main crop is a brief early-summer burst; the fall primocane crop is light',
    harvestTiming: 'early',
  },
  'raspberry:boyne': {
    flavorProfile: 'rich, dark, intensely flavored - one of the strongest-tasting red raspberries available',
    texture: 'small, soft berries that are fragile after picking',
    size: 'small',
    bestUses: 'zones 3 and 4 where most raspberries struggle; jam, juice, and fresh eating where intense flavor outweighs size',
    tradeoff: 'growers who want large, firm, shippable berries - Boyne is cold-hardy and intensely flavored but small and soft',
    harvestTiming: 'early',
  },
  'raspberry:killarney': {
    flavorProfile: 'sweet, balanced, and bright with good classic raspberry flavor',
    texture: 'medium-firm; holds up better to handling than Boyne',
    size: 'medium',
    bestUses: 'fresh eating, u-pick, and mid-climate gardens where disease resistance and reliable crops matter',
    tradeoff: 'extreme cold zones where Boyne\'s hardiness is the only option, or growers who need a very large firm berry',
    harvestTiming: 'early',
  },
  'raspberry:nova': {
    flavorProfile: 'clean, mild raspberry flavor with good sweetness',
    texture: 'medium-firm; nearly thornless canes make harvesting easy',
    size: 'medium',
    bestUses: 'cold-climate gardens (zones 3 to 5), u-pick operations, and mixed plantings where thornless canes are valued',
    tradeoff: 'growers who want the boldest possible flavor - Nova is reliable and mild, not intensely flavored like Boyne or Bristol',
    harvestTiming: 'mid',
  },
  'raspberry:latham': {
    flavorProfile: 'mild, classic red raspberry flavor; an honest workhorse rather than a showstopper',
    texture: 'medium softness; berries hold for a day or two after picking',
    size: 'medium',
    bestUses: 'cold gardens that need a dependable crop regardless of soil quality or neglect',
    tradeoff: 'growers who want the best eating quality - Latham tolerates poor conditions admirably but modern varieties like Killarney or Joan J surpass it on flavor and size',
    harvestTiming: 'mid',
  },
  'raspberry:josephine': {
    flavorProfile: 'sweet, full-flavored, and aromatic with excellent fresh-eating quality',
    texture: 'large, firm berries that hold their shape well',
    size: 'large',
    bestUses: 'fresh eating, farmers markets, and growers who want large impressive summer berries',
    tradeoff: 'extreme cold zones - Josephine is rated to zone 4 and less tested in zone 3 than Boyne or Nova',
    harvestTiming: 'mid',
  },
  'raspberry:heritage': {
    flavorProfile: 'slightly tart with classic concentrated fall raspberry flavor',
    texture: 'medium-firm; holds well for a day or two after picking',
    size: 'medium',
    bestUses: 'fresh eating, jam, and freezing from late summer through frost; the go-to standard fall-bearing variety',
    tradeoff: 'growers who want the largest possible fall berry or the earliest fall start - Caroline and Autumn Bliss beat Heritage on size and earliness respectively',
    harvestTiming: 'mid',
  },
  'raspberry:caroline': {
    flavorProfile: 'sweeter and richer than Heritage with bigger, more impressive fruit',
    texture: 'large, firm berries that outperform Heritage on both size and shelf life',
    size: 'large',
    bestUses: 'fresh eating, farmers markets, and anyone who wants a better Heritage with bigger sweeter berries',
    tradeoff: 'zone 3 gardens where Heritage\'s proven cold hardiness gives it the edge in the coldest winters',
    harvestTiming: 'mid',
  },
  'raspberry:autumn-bliss': {
    flavorProfile: 'sweet, well-balanced fall raspberry flavor with British breeding heritage',
    texture: 'medium-firm with good post-harvest life',
    size: 'medium',
    bestUses: 'Pacific Northwest gardens, early fall harvests before Heritage, and UK-influenced home gardens',
    tradeoff: 'very cold winters - Autumn Bliss is better proven in zone 6 to 9 Pacific Northwest climates than in extreme continental cold',
    harvestTiming: 'mid',
  },
  'raspberry:joan-j': {
    flavorProfile: 'rich, sweet fall raspberry flavor with large, dark-red berries',
    texture: 'large, firm, and smooth; spine-free canes make every harvest pleasant',
    size: 'large',
    bestUses: 'growers who want a completely spine-free fall raspberry experience with premium berry size',
    tradeoff: 'zone 3 reliability - Joan J performs best in zones 4 to 8 and is less tested in extreme cold than Heritage',
    harvestTiming: 'mid',
  },
  'raspberry:anne': {
    flavorProfile: 'mild, sweet, apricot-like flavor entirely unlike a red raspberry - a genuine palate surprise',
    texture: 'medium-soft yellow or golden berries',
    size: 'medium',
    bestUses: 'adding variety to a raspberry planting, children\'s gardens, and growers who want two distinct flavor profiles from one plant',
    tradeoff: 'growers who want the heaviest possible fall harvest - Anne\'s two-crop habit means smaller individual flushes compared to a dedicated fall-only primocane variety',
    harvestTiming: 'mid',
  },
  'raspberry:fallgold': {
    flavorProfile: 'very sweet, honey-like yellow raspberry with almost no tartness',
    texture: 'medium-soft yellow or golden berries; very sweet when ripe',
    size: 'medium',
    bestUses: 'fresh eating, children\'s gardens, and pairing with Heritage or Caroline for a colorful mixed fall harvest',
    tradeoff: 'growers who want the highest possible yield per plant - Fallgold produces reliably but not as heavily as Heritage or Caroline on a per-cane basis',
    harvestTiming: 'mid',
  },
  'raspberry:bristol': {
    flavorProfile: 'rich, intense black-raspberry flavor with deep fruity complexity unlike any red variety',
    texture: 'firm, glossy blue-black berries that hold up well to handling',
    size: 'large',
    bestUses: 'fresh eating, jam, baking, and any use where the unmistakable black-raspberry flavor is the point',
    tradeoff: 'growers who want to mix red and black raspberries for cross-pollination - black and red raspberries do not cross-pollinate; black varieties need a same-species companion',
    harvestTiming: 'early',
  },
  'raspberry:jewel': {
    flavorProfile: 'intensely flavored black raspberry with the richest, most complex taste of the Cornell-bred selections',
    texture: 'large, glossy, firm berries; the most impressive black raspberry for fresh eating',
    size: 'large',
    bestUses: 'fresh eating, premium jam, and growers who want the best-flavored black raspberry on the market',
    tradeoff: 'extreme cold zones below zone 4 - Jewel is not as cold-tested as some older selections',
    harvestTiming: 'mid',
  },
  'raspberry:allen': {
    flavorProfile: 'good black-raspberry flavor with classic intensity',
    texture: 'medium-firm berries suitable for fresh eating and processing',
    size: 'medium',
    bestUses: 'cross-pollination companion for Jewel and Bristol; reliable mid-season black raspberry',
    tradeoff: 'growers who want the single largest or most intensely flavored black variety - Jewel edges Allen out on both counts',
    harvestTiming: 'mid',
  },
  'blackberry:natchez': {
    flavorProfile: 'very sweet with low acidity and a clean finish - one of the sweetest large blackberries available',
    texture: 'large, elongated, very firm berries; excellent shelf life',
    size: 'very large',
    bestUses: 'fresh eating, farmers markets, and southern gardens (zones 6 to 9) where the earliest possible harvest is valued',
    tradeoff: 'cold-climate gardens - Natchez is not reliably hardy below zone 6 and performs best in the South',
    harvestTiming: 'early',
  },
  'blackberry:arapaho': {
    flavorProfile: 'sweet-tart balance with good classic blackberry depth',
    texture: 'medium-large, firm berries that handle well',
    size: 'medium',
    bestUses: 'thornless erect hedgerows, early-season harvests, and southern home gardens',
    tradeoff: 'cold-climate zones below 6 - Arapaho is best in zones 6 to 9',
    harvestTiming: 'early',
  },
  'blackberry:ouachita': {
    flavorProfile: 'sweet with balanced acidity and reliable flavor crop after crop',
    texture: 'large, firm berries with excellent post-harvest handling; white flowers add ornamental appeal',
    size: 'large',
    bestUses: 'fresh eating, farmers markets, and mid-season harvests where consistent large fruit and handling quality matter',
    tradeoff: 'cold-climate gardens below zone 6 - Ouachita is at its best in zones 6 to 9',
    harvestTiming: 'mid',
  },
  'blackberry:apache': {
    flavorProfile: 'excellent, balanced sweet-tart flavor - widely considered one of the best-tasting thornless blackberries',
    texture: 'large, firm berries with good density',
    size: 'large',
    bestUses: 'fresh eating and any planting where flavor is the top priority alongside ease of harvest',
    tradeoff: 'cold-climate zones below 6 - Apache is best in zones 6 to 9',
    harvestTiming: 'mid',
  },
  'blackberry:osage': {
    flavorProfile: 'sweet-tart with good blackberry character',
    texture: 'medium-large, firm berries on an erect thornless plant',
    size: 'medium',
    bestUses: 'mid-Atlantic and upper South gardens, and the most cold-hardy erect thornless option for zones 5 to 9',
    tradeoff: 'growers who want the largest possible berry - Ouachita and Apache edge Osage on fruit size',
    harvestTiming: 'mid',
  },
  'blackberry:triple-crown': {
    flavorProfile: 'sweet, complex, aromatic flavor with genuine depth - widely regarded as the best-tasting thornless blackberry',
    texture: 'large, glossy berries on semi-erect trailing canes',
    size: 'large',
    bestUses: 'fresh eating, jam, and any planting where flavor complexity is more important than easy-erect management',
    tradeoff: 'growers who want erect, self-supporting canes - Triple Crown is a trailing type that needs a trellis',
    harvestTiming: 'mid',
  },
  'blackberry:navaho': {
    flavorProfile: 'the sweetest flavor of any erect thornless variety, with very low acidity',
    texture: 'small, very firm berries that hold on the plant for weeks without softening',
    size: 'small',
    bestUses: 'fresh eating, extended harvest windows, and southern gardens where a compact sweet berry is preferred over large fruit',
    tradeoff: 'growers who want large, impressive berries - Navaho berries are small despite being among the sweetest',
    harvestTiming: 'mid',
  },
  'blackberry:hull-thornless': {
    flavorProfile: 'mild-sweet with classic blackberry character; less intense than Triple Crown',
    texture: 'large berries on semi-erect canes with very high yield per plant',
    size: 'large',
    bestUses: 'high-volume fresh eating, freezing, and processing where total harvest weight matters more than peak flavor',
    tradeoff: 'growers who want maximum flavor - Hull Thornless produces abundantly but Triple Crown or Apache surpass it on taste',
    harvestTiming: 'mid',
  },
  'blackberry:black-satin': {
    flavorProfile: 'moderately tart with classic blackberry sharpness; less sweet than Triple Crown or Apache',
    texture: 'medium-firm semi-erect berries',
    size: 'medium',
    bestUses: 'jam, baking, and mixed harvests where some tartness is welcome',
    tradeoff: 'fresh eating where sweetness is the priority - Black Satin is noticeably more tart than the best sweet varieties',
    harvestTiming: 'mid',
  },
  'blackberry:chester': {
    flavorProfile: 'mild, sweet, and pleasant - less acidic than most blackberries',
    texture: 'large, firm berries that hold well on the plant into August',
    size: 'large',
    bestUses: 'extending the blackberry season into the latest summer slot, fresh eating, and cold-climate gardens (hardy to zone 5)',
    tradeoff: 'growers who want an intense, complex blackberry flavor - Chester is mild and pleasant rather than boldly flavored',
    harvestTiming: 'late',
  },
  'blackberry:thornless-evergreen': {
    flavorProfile: 'rich, deep blackberry flavor with good complexity; the standard for Pacific Northwest fresh and processed blackberries',
    texture: 'medium-firm trailing canes; semi-evergreen in mild winters',
    size: 'medium',
    bestUses: 'Pacific Northwest and mild-winter West Coast gardens where the trailing type thrives and large volumes are wanted',
    tradeoff: 'cold-climate gardens below zone 6 - Thornless Evergreen is suited to mild-winter climates and does not survive hard freezes reliably',
    harvestTiming: 'late',
  },
  'blackberry:prime-ark-freedom': {
    flavorProfile: 'sweet-tart with good fresh blackberry flavor; slightly less complex than the best floricane varieties',
    texture: 'large, firm berries on thornless first-year canes',
    size: 'large',
    bestUses: 'growers who want a fall blackberry crop in year one without waiting for second-year canes; also produces a summer crop in year two',
    tradeoff: 'growers who want the very best eating flavor - established floricane varieties like Triple Crown or Apache edge out primocane types on peak taste',
    harvestTiming: 'mid',
  },
  'blackberry:prime-ark-traveler': {
    flavorProfile: 'sweet-tart with reliable fall blackberry flavor',
    texture: 'medium-large, firm thornless berries',
    size: 'medium',
    bestUses: 'short-season gardens where reliable fall ripening before first frost is critical; improves on Prime-Ark Freedom for northern primocane growing',
    tradeoff: 'growers in very long seasons who already have good floricane options - Traveler fills a short-season niche that floricane varieties handle just as well in long-season climates',
    harvestTiming: 'mid',
  },
  'strawberry:earliglow': {
    flavorProfile: 'intensely sweet and aromatic - the best flavor of any June-bearing strawberry, period',
    texture: 'small to medium, soft berries that bruise easily',
    size: 'small',
    bestUses: 'fresh eating immediately after picking, jam, and gardens where flavor intensity matters more than size or handling',
    tradeoff: 'u-pick operations or growers who need large, firm, shippable fruit - Earliglow berries are small, soft, and do not hold up to rough handling',
    harvestTiming: 'early',
  },
  'strawberry:honeoye': {
    flavorProfile: 'sweet with a bright acidic edge; classic strawberry flavor that is pleasant rather than exceptional',
    texture: 'large, firm, bright red berries that hold up to picking and short transport',
    size: 'large',
    bestUses: 'u-pick operations, cold climates (zone 3 with mulching), and growers who want reliable large yields over peak flavor',
    tradeoff: 'fresh eating connoisseurs who want the most intense flavor - Earliglow beats Honeoye on taste; Honeoye beats Earliglow on size and yield',
    harvestTiming: 'early',
  },
  'strawberry:cavendish': {
    flavorProfile: 'sweet, full, and well-balanced with clean strawberry character',
    texture: 'large, firm, dark red berries with good handling quality',
    size: 'large',
    bestUses: 'cold northern gardens (zones 3 to 5), fresh eating, and mixed plantings where reliable mid-season crops are the priority',
    tradeoff: 'warm-climate gardens - Cavendish is bred for cold hardiness and is less suited to zones 7 and above',
    harvestTiming: 'mid',
  },
  'strawberry:kent': {
    flavorProfile: 'sweet, classic, and full-flavored with excellent cold-climate performance',
    texture: 'large, dark red, firm berries',
    size: 'large',
    bestUses: 'northern gardens (zones 3 to 5), u-pick, and fresh eating where large cold-hardy berries are the goal',
    tradeoff: 'warm southern climates - Kent is bred for cold performance and less suited to zones 7 and above',
    harvestTiming: 'mid',
  },
  'strawberry:jewel-strawberry': {
    flavorProfile: 'classic, balanced strawberry flavor with good sweetness',
    texture: 'large, firm berries with the appearance and durability that made it the northeast standard',
    size: 'large',
    bestUses: 'northeastern home gardens, u-pick operations, and growers who want the time-tested standard for the region',
    tradeoff: 'growers in the Pacific Northwest or warm climates - Jewel is optimized for northeastern growing conditions',
    harvestTiming: 'mid',
  },
  'strawberry:chandler': {
    flavorProfile: 'bright, sweet, and aromatic - the California commercial standard for good reason',
    texture: 'very large, firm berries with excellent visual appeal',
    size: 'very large',
    bestUses: 'warm-climate gardens (zones 6 to 8), growers who want the largest possible June-bearing berry, and fresh-market presentations',
    tradeoff: 'cold-climate gardens - Chandler does not handle hard winters well and is not suitable for zones 3 to 5 without significant protection',
    harvestTiming: 'mid',
  },
  'strawberry:allstar': {
    flavorProfile: 'mild, sweet, and pleasant - dependable rather than exceptional',
    texture: 'large, light-colored, firm berries with good shelf life',
    size: 'large',
    bestUses: 'cold northern gardens, late-season June production, and mixed beds where disease resistance matters',
    tradeoff: 'growers who want peak flavor intensity - Allstar is mild and consistent; Earliglow or Jewel are better choices for the best-tasting harvest',
    harvestTiming: 'late',
  },
  'strawberry:sparkle': {
    flavorProfile: 'rich, deep, old-fashioned strawberry flavor with high acidity - the best jam strawberry of the bunch',
    texture: 'soft, fragile berries that must be used quickly after picking',
    size: 'medium',
    bestUses: 'jam, preserves, and growers who want late-season June berries with the most intense cooking flavor',
    tradeoff: 'fresh transport or u-pick - Sparkle is soft and fragile; it falls apart if not used the same day it is picked',
    harvestTiming: 'late',
  },
  'strawberry:albion': {
    flavorProfile: 'very sweet, rich, and aromatic - the best overall flavor of the day-neutral varieties',
    texture: 'very large, firm, conical berries with excellent post-harvest life',
    size: 'very large',
    bestUses: 'fresh eating, farmers markets, and warm-climate continuous production from spring through fall',
    tradeoff: 'cold-climate zones below 5 or very hot summers above 90 degrees - Albion performs best in mild to warm climates where temperatures stay in the sweet spot',
    harvestTiming: 'mid',
  },
  'strawberry:seascape': {
    flavorProfile: 'sweet, clean, and bright with reliable flavor across the long season',
    texture: 'large, firm berries with good handling quality',
    size: 'large',
    bestUses: 'Pacific Northwest home gardens, heat-tolerant continuous production, and growers who want a reliable day-neutral with proven performance',
    tradeoff: 'very cold gardens - Seascape is hardy to zone 4 but performs best in zones 6 to 9 where Pacific NW breeding shows best',
    harvestTiming: 'mid',
  },
  'strawberry:evie-2': {
    flavorProfile: 'sweet and pleasant with solid all-season flavor',
    texture: 'medium-large, firm berries with consistent appearance',
    size: 'medium',
    bestUses: 'northern day-neutral growers (zones 4 to 6) who need a heat-tolerant continuous producer where Albion underperforms in summer',
    tradeoff: 'warm climates where Albion or Seascape are the proven choices - Evie-2 fills a specific cool-summer niche',
    harvestTiming: 'mid',
  },
  'strawberry:ozark-beauty': {
    flavorProfile: 'sweet and aromatic with two distinctive flush peaks - spring and fall - rather than a continuous trickle',
    texture: 'large, firm berries with good fresh-eating quality',
    size: 'large',
    bestUses: 'cold northern gardens (zone 3 with mulching), growers who want two distinct harvest events rather than constant picking, and classic American home garden strawberry beds',
    tradeoff: 'growers who want continuous production all season - Ozark Beauty is an everbearing (two flushes), not a day-neutral (continuous); in midsummer it largely stops producing',
    harvestTiming: 'mid',
  },
  'strawberry:quinault': {
    flavorProfile: 'sweet, mild, and old-fashioned with good fresh-eating appeal',
    texture: 'large, soft berries; fragile after picking',
    size: 'large',
    bestUses: 'fresh eating at home and growers who want the classic large everbearing berry without fussing over day-neutral management',
    tradeoff: 'disease-prone sites or growers who need long shelf life - Quinault is less disease-resistant than modern varieties and soft berries do not hold',
    harvestTiming: 'mid',
  },
  'aronia:viking': {
    flavorProfile: 'intensely tart and astringent with concentrated dark berry depth - classic chokeberry character',
    texture: 'firm processing-berry texture; not pleasant raw in large quantities',
    size: 'small',
    bestUses: 'juice, syrup, wine, jam, dried berries, and any use where the powerful flavor is cooked or blended into something',
    tradeoff: 'growers who want a mild fresh-eating berry - aronia is not candy; raw berries are highly astringent and most people use them processed',
    harvestTiming: 'mid',
  },
  'aronia:nero': {
    flavorProfile: 'intensely tart and astringent matching Viking; essentially the same flavor profile',
    texture: 'firm processing-berry texture on a slightly less vigorous shrub than Viking',
    size: 'small',
    bestUses: 'same as Viking - juice, syrup, jam, dried berries; the best value as a companion to Viking for improved pollination',
    tradeoff: 'growers who want something visually or flavor-wise different from Viking in the same bed - Nero and Viking are nearly identical',
    harvestTiming: 'mid',
  },
  'aronia:mckenzie': {
    flavorProfile: 'similar tart-astringent aronia flavor; considered closest to pure Aronia melanocarpa taste',
    texture: 'firm berry flesh on the largest-growing cultivar (9 to 12 ft)',
    size: 'small',
    bestUses: 'larger properties, naturalized hedgerows, and growers who want a true-species aronia rather than a Sorbaronia hybrid',
    tradeoff: 'small gardens - McKenzie grows significantly larger than Viking or Nero and needs substantial space',
    harvestTiming: 'mid',
  },
  'aronia:autumn-magic': {
    flavorProfile: 'standard tart-astringent aronia flavor; confirmed true Aronia melanocarpa, not Sorbaronia',
    texture: 'firm processing-berry texture with outstanding fall foliage as a bonus',
    size: 'small',
    bestUses: 'ornamental-plus-edible plantings where fall color rivals or exceeds the value of the fruit harvest',
    tradeoff: 'growers who want maximum berry yield as the top priority - Autumn Magic produces well but its key selling point is its exceptional fall color, not the heaviest crop',
    harvestTiming: 'mid',
  },
  'aronia:iroquois-beauty': {
    flavorProfile: 'tart-astringent aronia character; same flavor as Viking in a much more compact plant',
    texture: 'firm berry flesh on a compact 2 to 3 ft tall, 4 to 5 ft wide shrub',
    size: 'small',
    bestUses: 'small gardens, foundation plantings, and mixed edible landscapes where the standard aronia is too large',
    tradeoff: 'maximum harvest volume - the compact habit limits total berry production compared to full-sized shrubs',
    harvestTiming: 'mid',
  },
  'aronia:low-scape-mound': {
    flavorProfile: 'tart-astringent aronia flavor in a very compact, groundcover-style plant',
    texture: 'firm berry flesh on an 18 to 24 inch mounding shrub',
    size: 'small',
    bestUses: 'containers, low borders, front-of-bed edible plantings, and any space where a full-sized shrub is impractical',
    tradeoff: 'growers who want a meaningful harvest - Low Scape Mound produces fruit but yields far less than upright varieties; it is primarily a landscaping plant',
    harvestTiming: 'mid',
  },
  'goji-berry:phoenix-tears': {
    flavorProfile: 'mildly sweet with a light herbal tang typical of goji; deep orange color signals ripeness',
    texture: 'compact, jellybean-shaped berries; trellis-friendly canes stay manageable',
    size: 'small',
    bestUses: 'cold-climate gardens (zone 3), container growing, and small-space edible plantings where hardiness is the overriding factor',
    tradeoff: 'growers who want the largest possible goji berries - Phoenix Tears is compact and cold-hardy first; Big Lifeberry beats it on fruit size',
    harvestTiming: 'mid',
  },
  'goji-berry:big-lifeberry': {
    flavorProfile: 'sweeter, juicier, and less herbal than traditional dried goji; noticeably different fresh from the supermarket berry',
    texture: 'larger, juicier berries than the species; selected for fresh eating quality',
    size: 'medium',
    bestUses: 'fresh eating from the plant and gardeners who want a goji genuinely worth eating raw',
    tradeoff: 'cold zones below 5 - Big Lifeberry is best in zones 5 to 9; Phoenix Tears handles zone 3 better',
    harvestTiming: 'mid',
  },
  'goji-berry:sweet-lifeberry': {
    flavorProfile: 'very sweet with almost no herbal bite - the mildest and sweetest goji on the market',
    texture: 'medium-large berries on a vigorous plant reaching 10 to 12 ft if unpruned',
    size: 'medium',
    bestUses: 'fresh eating, gardeners who find traditional goji too medicinal-tasting, and pairing with Big Lifeberry for complementary flavor profiles',
    tradeoff: 'growers who want a compact low-maintenance plant - Sweet Lifeberry is a vigorous grower reaching 10 to 12 ft unpruned and needs trellising or regular pruning to stay in bounds',
    harvestTiming: 'mid',
  },
  'goji-berry:crimson-star': {
    flavorProfile: 'tangy-sweet with bright red color and clean goji character',
    texture: 'large, firm, bright red berries on a medium 6 to 8 ft plant',
    size: 'large',
    bestUses: 'fresh eating, mixed edible borders, and growers who want a goji with consistently large, visually striking fruit',
    tradeoff: 'very cold winters below zone 4 - Crimson Star is best in zones 4 to 9; Phoenix Tears is the zone 3 choice',
    harvestTiming: 'mid',
  },
  'goji-berry:ningxia': {
    flavorProfile: 'concentrated, slightly bitter-sweet, and medicinal - the traditional dried wolfberry profile',
    texture: 'small, slightly chewy berries that dry well',
    size: 'small',
    bestUses: 'drying for tea, smoothie powders, and traditional medicinal uses where the classic wolfberry flavor is what you want',
    tradeoff: 'fresh eating - Ningxia is significantly more bitter and less pleasant raw than Big Lifeberry or Sweet Lifeberry',
    harvestTiming: 'mid',
  },
  'cranberry:ben-lear': {
    flavorProfile: 'deep, tart cranberry flavor with concentrated acidity and good color',
    texture: 'large, deep red berries that hold their shape after processing',
    size: 'large',
    bestUses: 'juice, sauce, and early-season fresh cranberry harvests; the most productive early variety',
    tradeoff: 'cold zones below 2 or gardens above zone 5 - Ben Lear is suited to zones 2 to 5 and grows less well in warmer regions',
    harvestTiming: 'early',
  },
  'cranberry:early-black': {
    flavorProfile: 'bright, tart cranberry flavor with deep dark-red color',
    texture: 'compact trailing habit; small to medium berries with good color',
    size: 'medium',
    bestUses: 'the earliest fresh cranberry of the season, juice, and sauce; one of the four historic "Big Four" commercial varieties',
    tradeoff: 'growers who want a variety suited to warm or dry climates - Early Black thrives in the cool, moist conditions of zones 4 to 7 and does not perform well outside that range',
    harvestTiming: 'early',
  },
  'cranberry:pilgrim': {
    flavorProfile: 'tart and juicy with lower pectin than most cranberries - makes thinner sauces and more pourable juice',
    texture: 'juicy, medium-large berries; notably frost-tolerant',
    size: 'medium',
    bestUses: 'juice, low-gel sauces, and cold-climate gardens where false-blossom disease or late frosts are concerns',
    tradeoff: 'growers who want the heaviest-yielding single variety - Stevens consistently out-produces Pilgrim on total crop weight',
    harvestTiming: 'mid',
  },
  'cranberry:stevens': {
    flavorProfile: 'standard tart cranberry flavor with balanced acidity; the commercial benchmark',
    texture: 'large, firm, consistent berries that hold well after harvest',
    size: 'large',
    bestUses: 'the most productive and reliable choice for home gardens; sauce, juice, and fresh storage',
    tradeoff: 'growers who want unusual flavor complexity or a very early harvest - Stevens is the dependable workhorse, not a specialty variety',
    harvestTiming: 'mid',
  },
  'cranberry:searles': {
    flavorProfile: 'tart cranberry flavor with good depth; the Wisconsin commercial standard since 1893',
    texture: 'medium-firm berries with reliable processing quality',
    size: 'medium',
    bestUses: 'upper Midwest and Great Lakes gardens, juice, sauce, and growers who want a time-tested heirloom with proven cold performance',
    tradeoff: 'very warm climates - Searles is best in zones 3 to 6 and is a Wisconsin specialty less optimized for warmer regions',
    harvestTiming: 'mid',
  },
  'cranberry:mcfarlin': {
    flavorProfile: 'classic tart cranberry flavor with good keeping quality',
    texture: 'medium-firm berries on a vigorous, disease-resistant plant',
    size: 'medium',
    bestUses: 'zones 5 to 6 gardens, disease-resistant plantings, and the historic "Big Four" cranberry collection',
    tradeoff: 'very cold zones below 5 or very warm zones above 6 - McFarlin\'s sweet spot is narrower than Stevens or Howes',
    harvestTiming: 'mid',
  },
  'cranberry:howes': {
    flavorProfile: 'firm, tart cranberry flavor with good keeping quality; the longest-stored fresh cranberry of the traditional varieties',
    texture: 'small, very firm, shiny berries that keep weeks longer than most after harvest',
    size: 'small',
    bestUses: 'long fresh storage, late-season sauce, and growers who want cranberries that hold in cold storage into the winter',
    tradeoff: 'growers who want maximum yield per plant - Howes produces reliably but not as heavily as Stevens',
    harvestTiming: 'late',
  },
  'lingonberry:koralle': {
    flavorProfile: 'sharply tart with concentrated, complex berry flavor - the definitive lingonberry taste that defines the European standard',
    texture: 'small, firm berries; compact upright plants yield two crops per year',
    size: 'small',
    bestUses: 'sauce, jam, syrup, and any use where the classic tart lingonberry flavor is the goal; the highest-yielding of the bunch',
    tradeoff: 'growers who want a mild fresh-eating berry - Koralle is quite tart raw and is at its best cooked or sweetened',
    harvestTiming: 'mid',
  },
  'lingonberry:red-pearl': {
    flavorProfile: 'milder and slightly sweeter than Koralle with larger, more approachable berries',
    texture: 'larger berries than Koralle; ripens 1 to 2 weeks earlier',
    size: 'medium',
    bestUses: 'serving as the primary pollinizer for Koralle, Linnaea, and other varieties at a 1:5 to 1:10 planting ratio; also edible with better mild flavor than Koralle',
    tradeoff: 'growers who want maximum yield from their pollinizer - Red Pearl is a lower-yielding variety chosen for pollination coverage, not maximum berry production',
    harvestTiming: 'early',
  },
  'lingonberry:sussi': {
    flavorProfile: 'tart lingonberry flavor similar to Koralle but on a slow-to-establish spreading plant',
    texture: 'small berries on a low spreading 7 to 10 inch plant',
    size: 'small',
    bestUses: 'secondary pollinizer alongside Red Pearl and spreading groundcover in acidic soil beds',
    tradeoff: 'growers who want productive yields quickly - Sussi is notably slow to establish and takes several years to reach full output',
    harvestTiming: 'mid',
  },
  'lingonberry:linnaea': {
    flavorProfile: 'tart, classic lingonberry character similar to Koralle with vigorous production',
    texture: 'small berries on a vigorous upright plant; smaller than Red Pearl',
    size: 'small',
    bestUses: 'cold northern gardens (zones 2 to 6) and high-yield sauce and jam production',
    tradeoff: 'warm climates above zone 6 - Linnaea is bred for cold performance and struggles in warmer zones',
    harvestTiming: 'mid',
  },
  'lingonberry:erntedank': {
    flavorProfile: 'standard tart lingonberry flavor with consistent yields across both spring and fall flushes',
    texture: 'small to medium berries on a 14 to 16 inch upright plant',
    size: 'small',
    bestUses: 'consistent two-crop production, German and Scandinavian-inspired gardens, and commercial-style home plantings where reliable volume matters',
    tradeoff: 'growers who want the most intensely flavored single berry - Erntedank is reliable and consistent rather than exceptional',
    harvestTiming: 'mid',
  },
  'lingonberry:ida': {
    flavorProfile: 'tart lingonberry flavor with notably large berries by lingonberry standards',
    texture: 'the largest berries of the group; upright 8 to 10 inch habit',
    size: 'medium',
    bestUses: 'fresh eating where lingonberry size matters, mixed beds with Red Pearl, and growers who want the most impressive-looking lingonberry fruit',
    tradeoff: 'very cold zones below 3 - Ida is rated to zone 3 and not tested as far north as Linnaea',
    harvestTiming: 'mid',
  },
};

const HARVEST_WINDOW_BY_TYPE: Partial<Record<FruitPollinationVariety['fruitType'], Record<FruitHarvestZoneBand, Record<FruitHarvestTiming, string>>>> = {
  apple: {
    cold: { early: 'late August', 'early-mid': 'early September', mid: 'mid September', 'mid-late': 'late September', late: 'early to mid October' },
    mid: { early: 'mid to late August', 'early-mid': 'late August to early September', mid: 'early to mid September', 'mid-late': 'mid to late September', late: 'late September to early October' },
    warm: { early: 'early to mid August', 'early-mid': 'mid to late August', mid: 'late August to early September', 'mid-late': 'early to mid September', late: 'mid to late September' },
  },
  pear: {
    cold: { early: 'mid August', 'early-mid': 'late August', mid: 'early September', 'mid-late': 'mid September', late: 'late September' },
    mid: { early: 'early to mid August', 'early-mid': 'mid to late August', mid: 'late August to early September', 'mid-late': 'early to mid September', late: 'mid to late September' },
    warm: { early: 'late July to early August', 'early-mid': 'mid August', mid: 'late August', 'mid-late': 'early September', late: 'mid September' },
  },
  plum: {
    cold: { early: 'early August', 'early-mid': 'mid August', mid: 'late August', 'mid-late': 'late August to early September', late: 'mid September' },
    mid: { early: 'late July to early August', 'early-mid': 'early to mid August', mid: 'mid to late August', 'mid-late': 'late August', late: 'early to mid September' },
    warm: { early: 'mid to late July', 'early-mid': 'late July to early August', mid: 'early to mid August', 'mid-late': 'mid to late August', late: 'late August to early September' },
  },
  cherry: {
    cold: { early: 'mid July', 'early-mid': 'late July', mid: 'late July to early August', 'mid-late': 'early August', late: 'mid August' },
    mid: { early: 'late June', 'early-mid': 'early July', mid: 'mid July', 'mid-late': 'late July', late: 'early August' },
    warm: { early: 'mid to late June', 'early-mid': 'late June', mid: 'early July', 'mid-late': 'mid July', late: 'late July' },
  },
  blueberry: {
    cold: { early: 'early July', 'early-mid': 'mid July', mid: 'late July', 'mid-late': 'early August', late: 'mid August' },
    mid: { early: 'late June to early July', 'early-mid': 'early to mid July', mid: 'mid July', 'mid-late': 'late July to early August', late: 'early to mid August' },
    warm: { early: 'mid to late June', 'early-mid': 'late June to early July', mid: 'mid July', 'mid-late': 'late July', late: 'early August' },
  },
  peach: {
    cold: { early: 'late July', 'early-mid': 'early August', mid: 'mid August', 'mid-late': 'late August', late: 'early September' },
    mid: { early: 'mid to late July', 'early-mid': 'late July to early August', mid: 'early to mid August', 'mid-late': 'mid to late August', late: 'late August to early September' },
    warm: { early: 'early to mid July', 'early-mid': 'mid to late July', mid: 'late July to early August', 'mid-late': 'mid August', late: 'late August' },
  },
  nectarine: {
    cold: { early: 'late July', 'early-mid': 'early August', mid: 'mid August', 'mid-late': 'late August', late: 'early September' },
    mid: { early: 'mid to late July', 'early-mid': 'late July to early August', mid: 'early to mid August', 'mid-late': 'mid to late August', late: 'late August to early September' },
    warm: { early: 'early to mid July', 'early-mid': 'mid to late July', mid: 'late July to early August', 'mid-late': 'mid August', late: 'late August' },
  },
  apricot: {
    cold: { early: 'mid July', 'early-mid': 'late July', mid: 'early August', 'mid-late': 'mid August', late: 'late August' },
    mid: { early: 'early to mid July', 'early-mid': 'mid to late July', mid: 'late July to early August', 'mid-late': 'mid August', late: 'late August' },
    warm: { early: 'late June to early July', 'early-mid': 'mid July', mid: 'late July', 'mid-late': 'early August', late: 'mid August' },
  },
  mulberry: {
    cold: { early: 'late June', 'early-mid': 'early July', mid: 'mid July', 'mid-late': 'late July', late: 'early August' },
    mid: { early: 'mid June', 'early-mid': 'late June', mid: 'early July', 'mid-late': 'mid July', late: 'late July' },
    warm: { early: 'late May to early June', 'early-mid': 'mid June', mid: 'late June', 'mid-late': 'early July', late: 'mid July' },
  },
  elderberry: {
    cold: { early: 'mid August', 'early-mid': 'late August', mid: 'late August to early September', 'mid-late': 'mid September', late: 'late September' },
    mid: { early: 'late July to early August', 'early-mid': 'mid August', mid: 'late August', 'mid-late': 'early September', late: 'mid September' },
    warm: { early: 'mid to late July', 'early-mid': 'early August', mid: 'mid August', 'mid-late': 'late August', late: 'early September' },
  },
  fig: {
    cold: { early: 'late August', 'early-mid': 'early September', mid: 'mid September', 'mid-late': 'late September', late: 'October' },
    mid: { early: 'mid August', 'early-mid': 'late August', mid: 'September', 'mid-late': 'late September', late: 'October' },
    warm: { early: 'late July', 'early-mid': 'mid August', mid: 'late August', 'mid-late': 'September', late: 'late September to October' },
  },
  serviceberry: {
    cold: { early: 'late June', 'early-mid': 'early July', mid: 'mid July', 'mid-late': 'late July', late: 'early August' },
    mid: { early: 'mid June', 'early-mid': 'late June', mid: 'early July', 'mid-late': 'mid July', late: 'late July' },
    warm: { early: 'late May', 'early-mid': 'early June', mid: 'mid June', 'mid-late': 'late June', late: 'early July' },
  },
  quince: {
    cold: { early: 'late September', 'early-mid': 'early October', mid: 'mid October', 'mid-late': 'late October', late: 'late October to early November' },
    mid: { early: 'mid September', 'early-mid': 'late September', mid: 'early to mid October', 'mid-late': 'late October', late: 'early November' },
    warm: { early: 'early September', 'early-mid': 'mid September', mid: 'late September', 'mid-late': 'early October', late: 'mid to late October' },
  },
  grape: {
    cold: { early: 'late August', 'early-mid': 'early September', mid: 'mid September', 'mid-late': 'late September', late: 'early October' },
    mid: { early: 'mid August', 'early-mid': 'late August', mid: 'early to mid September', 'mid-late': 'mid to late September', late: 'late September' },
    warm: { early: 'early to mid August', 'early-mid': 'mid August', mid: 'late August', 'mid-late': 'early September', late: 'mid September' },
  },
  kiwi: {
    cold: { early: 'late September', 'early-mid': 'early October', mid: 'mid October', 'mid-late': 'late October', late: 'early November' },
    mid: { early: 'mid September', 'early-mid': 'late September', mid: 'early October', 'mid-late': 'mid October', late: 'late October' },
    warm: { early: 'early September', 'early-mid': 'mid September', mid: 'late September', 'mid-late': 'early October', late: 'mid October' },
  },
  'hardy-kiwi': {
    cold: { early: 'early September', 'early-mid': 'mid September', mid: 'late September', 'mid-late': 'early October', late: 'mid October' },
    mid: { early: 'late August', 'early-mid': 'early September', mid: 'mid September', 'mid-late': 'late September', late: 'early October' },
    warm: { early: 'mid August', 'early-mid': 'late August', mid: 'early September', 'mid-late': 'mid September', late: 'late September' },
  },
  gooseberry: {
    cold: { early: 'early July', 'early-mid': 'mid July', mid: 'late July', 'mid-late': 'early August', late: 'mid August' },
    mid: { early: 'late June', 'early-mid': 'early July', mid: 'mid July', 'mid-late': 'late July', late: 'early August' },
    warm: { early: 'mid June', 'early-mid': 'late June', mid: 'early July', 'mid-late': 'mid July', late: 'late July' },
  },
  currant: {
    cold: { early: 'early July', 'early-mid': 'mid July', mid: 'late July', 'mid-late': 'early August', late: 'mid August' },
    mid: { early: 'late June', 'early-mid': 'early July', mid: 'mid July', 'mid-late': 'late July', late: 'early August' },
    warm: { early: 'mid June', 'early-mid': 'late June', mid: 'early July', 'mid-late': 'mid July', late: 'late July' },
  },
  jujube: {
    cold: { early: 'late September', 'early-mid': 'early October', mid: 'mid October', 'mid-late': 'late October', late: 'early November' },
    mid: { early: 'mid September', 'early-mid': 'late September', mid: 'early to mid October', 'mid-late': 'late October', late: 'early November' },
    warm: { early: 'early September', 'early-mid': 'mid September', mid: 'late September', 'mid-late': 'mid October', late: 'late October' },
  },
  pawpaw: {
    cold: { early: 'late September', 'early-mid': 'early October', mid: 'mid October', 'mid-late': 'late October', late: 'late October' },
    mid: { early: 'early September', 'early-mid': 'mid September', mid: 'late September', 'mid-late': 'early October', late: 'mid October' },
    warm: { early: 'late August', 'early-mid': 'early September', mid: 'mid September', 'mid-late': 'late September', late: 'early October' },
  },
  persimmon: {
    cold: { early: 'early October', 'early-mid': 'mid October', mid: 'late October', 'mid-late': 'early November', late: 'mid November' },
    mid: { early: 'late September', 'early-mid': 'early October', mid: 'mid to late October', 'mid-late': 'early November', late: 'mid November' },
    warm: { early: 'mid September', 'early-mid': 'late September', mid: 'October', 'mid-late': 'late October', late: 'early November' },
  },
  mango: {
    cold: { early: 'late July', 'early-mid': 'August', mid: 'late August to September', 'mid-late': 'September', late: 'late September to October' },
    mid: { early: 'July', 'early-mid': 'late July to August', mid: 'August', 'mid-late': 'late August to September', late: 'September to October' },
    warm: { early: 'June to early July', 'early-mid': 'July', mid: 'late July to August', 'mid-late': 'August to September', late: 'September' },
  },
  honeyberry: {
    cold: { early: 'late May to early June', 'early-mid': 'early June', mid: 'mid June', 'mid-late': 'late June', late: 'late June to early July' },
    mid: { early: 'mid May', 'early-mid': 'late May', mid: 'early June', 'mid-late': 'mid June', late: 'late June' },
    warm: { early: 'late April to early May', 'early-mid': 'mid May', mid: 'late May', 'mid-late': 'late May to early June', late: 'early June' },
  },
  hazelnut: {
    cold: { early: 'early September', 'early-mid': 'mid September', mid: 'late September', 'mid-late': 'early October', late: 'mid October' },
    mid: { early: 'late August to early September', 'early-mid': 'early to mid September', mid: 'mid September', 'mid-late': 'late September', late: 'early October' },
    warm: { early: 'mid to late August', 'early-mid': 'late August', mid: 'early September', 'mid-late': 'mid September', late: 'late September' },
  },
  raspberry: {
    cold: { early: 'late June to mid July', 'early-mid': 'early to mid July', mid: 'mid July to late August', 'mid-late': 'August to September', late: 'September to October' },
    mid: { early: 'mid to late June', 'early-mid': 'late June to early July', mid: 'early July to mid August', 'mid-late': 'mid August to September', late: 'September' },
    warm: { early: 'early to mid June', 'early-mid': 'mid to late June', mid: 'late June to early August', 'mid-late': 'August', late: 'August to early September' },
  },
  blackberry: {
    cold: { early: 'early to mid July', 'early-mid': 'mid July', mid: 'late July to August', 'mid-late': 'August', late: 'mid to late August' },
    mid: { early: 'late June to early July', 'early-mid': 'early to mid July', mid: 'mid July to early August', 'mid-late': 'early to mid August', late: 'mid August' },
    warm: { early: 'mid to late June', 'early-mid': 'late June', mid: 'early to mid July', 'mid-late': 'mid to late July', late: 'late July to early August' },
  },
  strawberry: {
    cold: { early: 'late May to early June', 'early-mid': 'early June', mid: 'mid June', 'mid-late': 'late June', late: 'late June to mid July' },
    mid: { early: 'mid to late May', 'early-mid': 'late May', mid: 'early to mid June', 'mid-late': 'mid to late June', late: 'late June' },
    warm: { early: 'early to mid May', 'early-mid': 'mid May', mid: 'late May to mid June', 'mid-late': 'mid June', late: 'mid to late June' },
  },
  aronia: {
    cold: { early: 'early August', 'early-mid': 'mid August', mid: 'late August to early September', 'mid-late': 'mid September', late: 'late September' },
    mid: { early: 'late July to early August', 'early-mid': 'early to mid August', mid: 'mid to late August', 'mid-late': 'early September', late: 'mid September' },
    warm: { early: 'mid to late July', 'early-mid': 'late July', mid: 'early to mid August', 'mid-late': 'mid August', late: 'late August to early September' },
  },
  'goji-berry': {
    cold: { early: 'late July to early August', 'early-mid': 'early to mid August', mid: 'August to September', 'mid-late': 'September', late: 'September to early October' },
    mid: { early: 'mid to late July', 'early-mid': 'late July to early August', mid: 'August', 'mid-late': 'late August to September', late: 'September' },
    warm: { early: 'early to mid July', 'early-mid': 'mid to late July', mid: 'late July to August', 'mid-late': 'August', late: 'late August to early September' },
  },
  cranberry: {
    cold: { early: 'early to mid September', 'early-mid': 'mid September', mid: 'late September to early October', 'mid-late': 'mid October', late: 'mid to late October' },
    mid: { early: 'late August to early September', 'early-mid': 'early to mid September', mid: 'mid to late September', 'mid-late': 'late September to early October', late: 'early October' },
    warm: { early: 'mid to late August', 'early-mid': 'late August', mid: 'early to mid September', 'mid-late': 'mid September', late: 'late September' },
  },
  lingonberry: {
    cold: { early: 'late July to early August', 'early-mid': 'early August', mid: 'mid August to early October', 'mid-late': 'September to October', late: 'October' },
    mid: { early: 'mid to late July', 'early-mid': 'late July to early August', mid: 'early August to late September', 'mid-late': 'September', late: 'late September to October' },
    warm: { early: 'early to mid July', 'early-mid': 'mid July', mid: 'mid July to mid September', 'mid-late': 'late August to September', late: 'September to early October' },
  },
};

function inferHarvestTiming(variety: FruitPollinationVariety): FruitHarvestTiming {
  if (variety.notes?.some(note => /late season|later season|later than/i.test(note))) return 'late';
  if (variety.notes?.some(note => /early/i.test(note))) return 'early';
  return 'mid';
}

function zoneToHarvestBand(zone: number | null): FruitHarvestZoneBand {
  if (!zone) return 'mid';
  if (zone <= 5) return 'cold';
  if (zone >= 8) return 'warm';
  return 'mid';
}

function fallbackInsight(variety: FruitPollinationVariety): FruitVarietyInsight {
  if (variety.pollinationRole === 'male-pollinizer') {
    return {
      flavorProfile: 'little to no fruit value because it is grown for pollen',
      texture: 'not a crop vine first',
      size: 'medium',
      bestUses: 'supplying pollen to nearby fruiting vines',
      tradeoff: 'growers who expect this plant to carry the main edible harvest',
      harvestTiming: 'mid',
    };
  }

  switch (variety.fruitType) {
    case 'apple':
      return {
        flavorProfile: 'sweet with a crisp tart finish',
        texture: 'firm fresh-eating flesh',
        size: 'medium',
        bestUses: 'fresh eating and mixed kitchen use',
        tradeoff: 'growers searching for a sharply specialized cider, storage, or dessert apple',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'pear':
      return {
        flavorProfile: 'sweet, gently perfumed flavor',
        texture: 'juicy, dessert-style texture',
        size: 'medium',
        bestUses: 'fresh eating and general kitchen use',
        tradeoff: 'growers looking for a sharply defined cooking or ultra-crisp specialty pear',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'plum':
      return variety.speciesGroup === 'european'
        ? {
            flavorProfile: 'deep sweetness with prune-like richness',
            texture: 'firm flesh that usually handles cooking well',
            size: 'medium',
            bestUses: 'fresh eating, baking, and preserving',
            tradeoff: 'growers who want the juiciest early dessert plum or a very soft fresh-only texture',
            harvestTiming: inferHarvestTiming(variety),
          }
        : {
            flavorProfile: 'sweet-tart balance with lively acidity',
            texture: 'juicy dessert-style flesh',
            size: 'medium',
            bestUses: 'fresh eating, sauce, and mixed home-orchard use',
            tradeoff: 'drying, firm prune-style kitchen work, or growers who want a very late European-plum season',
            harvestTiming: inferHarvestTiming(variety),
          };
    case 'cherry':
      return variety.speciesGroup === 'tart'
        ? {
            flavorProfile: 'bright acidity with concentrated red-fruit bite',
            texture: 'juicy flesh suited to preserving',
            size: 'medium',
            bestUses: 'baking, preserving, and juice',
            tradeoff: 'growers who want a sweet dessert cherry for fresh snacking',
            harvestTiming: inferHarvestTiming(variety),
          }
        : {
            flavorProfile: 'sweet flavor with classic dessert-cherry richness',
            texture: 'firm, juicy flesh',
            size: 'large',
            bestUses: 'fresh eating',
            tradeoff: 'heavy preserving use or growers who want tart pie-cherry character',
            harvestTiming: inferHarvestTiming(variety),
          };
    case 'blueberry':
      return {
        flavorProfile: 'sweet-tart flavor with concentrated berry notes',
        texture: 'fresh-picking berry texture',
        size: 'medium',
        bestUses: 'fresh eating and freezing',
        tradeoff: 'growers who want a strongly distinctive size or season niche',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'peach':
    case 'nectarine':
      return {
        flavorProfile: 'sweet flavor with soft stone-fruit aromatics',
        texture: 'juicy flesh',
        size: 'large',
        bestUses: 'fresh eating and backyard orchard harvests',
        tradeoff: 'growers who need an unusual niche beyond a dependable home peach or nectarine',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'apricot':
      return {
        flavorProfile: 'sweet flavor with light tang and floral lift',
        texture: 'tender, juicy flesh',
        size: 'medium',
        bestUses: 'fresh eating, jam, drying, and early stone-fruit harvests',
        tradeoff: 'growers who want very firm shipping fruit or the latest summer harvest slot',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'mango':
      return {
        flavorProfile: 'sweet tropical richness with low acidity',
        texture: 'juicy, smooth flesh',
        size: 'large',
        bestUses: 'fresh eating, smoothies, chutney, and tropical dessert fruit',
        tradeoff: 'cold-climate outdoor planting or growers who want a firm, low-mess fruit',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'pawpaw':
      return {
        flavorProfile: 'custardy tropical flavor with banana-like sweetness',
        texture: 'soft, creamy flesh',
        size: 'large',
        bestUses: 'fresh eating, freezing pulp, and rich dessert-style uses',
        tradeoff: 'long shipping, crisp texture, or fruit that needs to sit around after picking',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'persimmon':
      return {
        flavorProfile: 'sweet honeyed flavor with soft richness',
        texture: 'soft dessert flesh when fully ripe',
        size: 'medium',
        bestUses: 'fresh eating, drying, and late-season sweet preserves',
        tradeoff: 'early harvests, tart flavor, or growers who want a crisp acidic fruit',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'mulberry':
      return {
        flavorProfile: 'sweet berry notes with dark-fruit depth',
        texture: 'juicy, soft berry flesh',
        size: 'medium',
        bestUses: 'fresh eating, jam, syrup, and long-picking harvests',
        tradeoff: 'firm transport, spotless harvests, or growers who want dry, crisp fruit',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'elderberry':
      return {
        flavorProfile: 'dark, winey flavor with concentrated depth',
        texture: 'soft processing berry texture',
        size: 'small',
        bestUses: 'syrup, jelly, wine, and cooked preserves',
        tradeoff: 'fresh eating straight from the bush or growers who want a firm snack berry',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'fig':
      return {
        flavorProfile: 'sweet, honeyed richness',
        texture: 'soft, jammy flesh',
        size: 'medium',
        bestUses: 'fresh eating, drying, and preserves',
        tradeoff: 'rough transport, crisp texture, or growers who need a very firm kitchen fruit',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'grape':
      return {
        flavorProfile: 'sweet flavor with a clean tart edge',
        texture: 'juicy flesh with a tender skin',
        size: 'medium',
        bestUses: 'fresh eating, juice, and bunch harvesting',
        tradeoff: 'growers who want dense flesh, long baking use, or a firm slicing fruit',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'pomegranate':
      return {
        flavorProfile: 'sweet-tart flavor with bright, refreshing acidity',
        texture: 'crisp, juicy arils',
        size: 'large',
        bestUses: 'fresh eating, juice, syrup, and bright garnish use',
        tradeoff: 'growers who want soft, seedless fruit or a low-acid dessert crop',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'passion-fruit':
      return {
        flavorProfile: 'intense sweet-tart flavor with tropical perfume',
        texture: 'juicy pulp with crunch from the seeds',
        size: 'medium',
        bestUses: 'fresh pulp, drinks, desserts, and freezing',
        tradeoff: 'growers who want mild flavor, firm slicing flesh, or seedless fruit',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'serviceberry':
      return {
        flavorProfile: 'sweet berry notes with almond-like depth',
        texture: 'tender berry texture',
        size: 'small',
        bestUses: 'fresh eating, baking, jam, and freezing',
        tradeoff: 'giant fruit, long hanging after ripeness, or growers who want a firm market berry',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'quince':
      return {
        flavorProfile: 'highly aromatic flavor with strong tartness',
        texture: 'hard flesh that softens only after cooking',
        size: 'large',
        bestUses: 'jelly, paste, poaching, and fragrant preserving',
        tradeoff: 'fresh eating straight from the tree or growers who want immediate dessert fruit',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'hardy-kiwi':
      return {
        flavorProfile: 'sweet-tart flavor with bright green-fruit notes',
        texture: 'smooth, juicy flesh',
        size: 'small',
        bestUses: 'fresh eating and handful-sized snacking fruit',
        tradeoff: 'big slicing fruit, dry texture, or growers who want something that stores like apples',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'kiwi':
      return {
        flavorProfile: 'sweet-tart flavor with classic kiwi depth',
        texture: 'smooth, juicy flesh',
        size: 'large',
        bestUses: 'fresh eating, slicing, and standard fuzzy-kiwi harvests',
        tradeoff: 'growers who want a tiny snack fruit, very early ripening, or no need for trellising',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'gooseberry':
      return {
        flavorProfile: 'bright sweet-tart berry flavor',
        texture: 'firm berry flesh',
        size: 'medium',
        bestUses: 'fresh eating, pies, jam, and freezing',
        tradeoff: 'soft dessert texture or growers who want a very mild berry',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'currant':
      return {
        flavorProfile: 'sharp, concentrated flavor with deep berry intensity',
        texture: 'soft processing-berry texture',
        size: 'small',
        bestUses: 'jelly, syrup, baking, and strong-flavored preserves',
        tradeoff: 'big sweet snack berries or growers who want low-acid fruit',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'jujube':
      return {
        flavorProfile: 'sweet, apple-like flavor that deepens as it dries',
        texture: 'crisp when fresh, denser as it matures',
        size: 'small',
        bestUses: 'fresh eating, drying, and warm-climate snacking orchards',
        tradeoff: 'juicy melting flesh or growers who want a berry-like fruit',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'honeyberry':
      return {
        flavorProfile: 'sweet-tart berry with blueberry-like depth and early-spring timing',
        texture: 'soft, juicy berry flesh',
        size: 'medium',
        bestUses: 'fresh eating and the earliest berry harvest of any cold-hardy shrub',
        tradeoff: 'growers who want a large, firm, long-storing berry',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'hazelnut':
      return {
        flavorProfile: 'rich hazelnut flavor with good sweetness',
        texture: 'medium kernel in a thin shell',
        size: 'medium',
        bestUses: 'fresh cracking, roasting, and home orchard nut production',
        tradeoff: 'growers who need the largest nut or a dedicated pollinizer-only variety',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'raspberry':
      return variety.speciesGroup === 'black'
        ? {
            flavorProfile: 'intense black-raspberry flavor with rich, deep complexity unlike any red variety',
            texture: 'firm, glossy blue-black berries',
            size: 'medium',
            bestUses: 'fresh eating, jam, and any use where the unmistakable black-raspberry flavor is the point',
            tradeoff: 'growers who want to mix red and black raspberries for cross-pollination - they do not cross-pollinate; black varieties need a same-species companion',
            harvestTiming: inferHarvestTiming(variety),
          }
        : {
            flavorProfile: 'bright, sweet-tart raspberry flavor with classic berry character',
            texture: 'soft, juicy berry flesh',
            size: 'medium',
            bestUses: 'fresh eating, jam, and freezing',
            tradeoff: 'growers who want the firmest, most transportable berry',
            harvestTiming: inferHarvestTiming(variety),
          };
    case 'blackberry':
      return {
        flavorProfile: 'sweet-tart blackberry flavor with dark fruit depth',
        texture: 'medium-firm berry flesh',
        size: 'large',
        bestUses: 'fresh eating, jam, and summer berry harvests',
        tradeoff: 'growers who want maximum flavor complexity - Triple Crown leads the field on taste',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'strawberry':
      return variety.speciesGroup === 'day-neutral'
        ? {
            flavorProfile: 'sweet, continuous berry flavor through the growing season',
            texture: 'medium-firm berries produced from spring through fall',
            size: 'medium',
            bestUses: 'continuous fresh eating and warm-climate season-long production',
            tradeoff: 'growers in very cold climates or very hot summers where day-neutral production falters',
            harvestTiming: inferHarvestTiming(variety),
          }
        : {
            flavorProfile: 'classic sweet strawberry flavor with a concentrated flush harvest',
            texture: 'firm, classic berry texture',
            size: 'large',
            bestUses: 'fresh eating, jam, freezing, and u-pick operations',
            tradeoff: 'growers who want continuous production all season rather than a single intense June harvest',
            harvestTiming: inferHarvestTiming(variety),
          };
    case 'aronia':
      return {
        flavorProfile: 'intensely tart and astringent with concentrated dark berry depth',
        texture: 'firm processing-berry texture',
        size: 'small',
        bestUses: 'juice, syrup, jam, dried berries, and health-focused preparations',
        tradeoff: 'fresh eating - aronia is highly astringent raw and best cooked or blended',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'goji-berry':
      return {
        flavorProfile: 'mildly sweet with a light herbal tang',
        texture: 'small to medium berries on arching canes',
        size: 'small',
        bestUses: 'fresh eating from the plant, drying, and smoothie additions',
        tradeoff: 'growers who want a mild sweet berry - goji has a distinctive herbal character not everyone enjoys raw',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'cranberry':
      return {
        flavorProfile: 'sharp, tart cranberry flavor with concentrated acidity',
        texture: 'firm berries from low trailing plants',
        size: 'medium',
        bestUses: 'sauce, juice, and fall preserving',
        tradeoff: 'fresh eating straight from the plant or growers who skip the cross-pollination requirement',
        harvestTiming: inferHarvestTiming(variety),
      };
    case 'lingonberry':
      return {
        flavorProfile: 'sharply tart with concentrated berry depth and classic Scandinavian character',
        texture: 'small, firm berries on low evergreen groundcover',
        size: 'small',
        bestUses: 'sauce, jam, syrup, and traditional preparations',
        tradeoff: 'growers who want a mild sweet berry - lingonberry is quite tart and at its best cooked or sweetened',
        harvestTiming: inferHarvestTiming(variety),
      };
    default:
      return {
        flavorProfile: 'sweet with a light acidic edge',
        texture: 'juicy, everyday-eating texture',
        size: 'medium',
        bestUses: 'fresh eating and mixed home harvest use',
        tradeoff: 'growers looking for a more sharply specialized variety',
        harvestTiming: inferHarvestTiming(variety),
      };
  }
}

export function getFruitVarietyInsight(variety: FruitPollinationVariety): FruitVarietyInsight {
  return FRUIT_VARIETY_INSIGHTS[`${variety.fruitType}:${variety.id}`]
    ?? FRUIT_VARIETY_INSIGHTS[variety.id]
    ?? fallbackInsight(variety);
}

export function getFruitVarietyHarvestWindow(variety: FruitPollinationVariety, zone: number | null): string {
  const insight = getFruitVarietyInsight(variety);
  const zoneBand = zoneToHarvestBand(zone);
  const directWindow = insight.harvestWindowByZone?.[zoneBand];
  if (directWindow) return directWindow;

  const byType = HARVEST_WINDOW_BY_TYPE[variety.fruitType];
  const fallbackWindow = byType?.[zoneBand]?.[insight.harvestTiming];
  if (fallbackWindow) return fallbackWindow;

  return insight.harvestTiming === 'early-mid'
    ? 'early to mid season'
    : insight.harvestTiming === 'mid-late'
      ? 'mid to late season'
      : `${insight.harvestTiming} season`;
}
